// src/pages/PartyPage.js
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { Copy } from 'lucide-react';
import Tesseract from 'tesseract.js';
import { useParams } from "react-router-dom";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
// eslint-disable-next-line no-unused-vars
import {
  fetchPartyAndMe,
  fetchPartyRecords,
  fetchLatestGateLog,          // 레거시 폴백용(analyses)
  savePartyAnalysis,           // __rec__ 저장에 계속 사용
  clearPartyAnalysesClient,
  disbandPartyClient,
  subscribePartyAveragesPinnedFirst,
  savePartyAverageSnapshot,
  // 👇 새로 추가: 메타/전용 로그 기반
  savePartyGateSelection,
  subscribePartyGateName,
  ensurePartyExpireAt,
} from "../firebaseClient";
import StepProgressBar from '../components/StepProgressBar';
import ControlsBar from '../components/ControlsBar';
import ScreenCapture from '../components/ScreenCapture';
import TraitsList from '../components/TraitsList';
import SavedRaidsPanelLite from '../components/SavedRaidsPanelLite';
import AppModal from "../components/AppModal";
import useMinDelay from '../hooks/useMinDelay';
import Loader from "../components/Loader";
import ExtendExpireButton from '../components/ExtendExpireButton';

import { ROI, ROI_SECOND_PASS_16, ROI_SECOND_PASS_18 } from '../constants/battle/roi';
import { RAID_NAMES } from '../constants/battle/raids';
import { JOB_CONFIG, KNOWN_JOBS, ROLE_COMMON_TRAITS } from '../constants/battle/jobs';
import { fetchLoaSummary } from '../lib/loaClient';


/* =========================================================
 * 관문 유틸
 * ======================================================= */
function stripGate(name) {
  return String(name || "").replace(/\s*\d+\s*관문\s*$/i, "").trim();
}
function getGateNumber(name) {
  const m = String(name || "").match(/(\d+)\s*관문\s*$/i);
  return m ? parseInt(m[1], 10) : null;
}
function getGateOptions(baseRaidName) {
  // RAID_NAMES(문자열 배열) 기준으로 관문 목록을 뽑는다.
  const arr = (RAID_NAMES || [])
    .filter(n => stripGate(n) === stripGate(baseRaidName));
  return arr.sort((a, b) => {
    const ga = getGateNumber(a) ?? 0;
    const gb = getGateNumber(b) ?? 0;
    return ga - gb;
  });
}

/* =========================================================
 * 생성일 관련 도우미
 * ======================================================= */

function toDateFromFirestore(ts) {
  if (!ts) return null;
  if (typeof ts.toMillis === 'function') return new Date(ts.toMillis());
  if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
  const n = Date.parse(String(ts));
  return Number.isFinite(n) ? new Date(n) : null;
}
function fmtKST(d) {
  if (!d) return '-';
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  }).format(d);
}


/* =========================================================
 * 기본 상수/ROI 프리셋
 * ======================================================= */
const MYCHAR_ROI = ROI?.myCharCheck || { x: 1211, y: 87, w: 50, h: 28 };
const MYCHAR_ROI2 = ROI?.myCharCheck2 || { x: 1269, y: 87, w: 50, h: 28 };

const ROI_PRESETS = {
  idle: { x: 859, y: 1, w: 1060, h: 690 },
  profile: { x: 859, y: 1, w: 1060, h: 690 },
  battleMain: { x: 447, y: 1, w: 1472, h: 902 },
  battleMain2: { x: 447, y: 1, w: 1472, h: 902 },
  done: { x: 447, y: 1, w: 1472, h: 902 },
};


/* =========================================================
 * 문자열 유사도/매칭 유틸
 * ======================================================= */
function levenshtein(a, b) {
  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
  }
  return dp[a.length][b.length];
}
function similarity(a, b) {
  if (!a || !b) return 0;
  if (!a.length && !b.length) return 1;
  return 1 - levenshtein(a, b) / Math.max(a.length, b.length);
}
function uniqueMatchWithScores(ocrTexts, candidates) {
  const pairs = [];
  for (let i = 0; i < ocrTexts.length; i++) {
    const src = (ocrTexts[i] || '').trim();
    for (let j = 0; j < candidates.length; j++) {
      const tgt = candidates[j];
      const score = similarity(src, tgt);
      pairs.push({ i, j, score });
    }
  }
  pairs.sort((a, b) => b.score - a.score);
  const usedRows = new Set();
  const usedCols = new Set();
  const results = Array(ocrTexts.length).fill(null);
  for (const { i, j, score } of pairs) {
    if (usedRows.has(i) || usedCols.has(j)) continue;
    results[i] = { best: candidates[j], score, index: j };
    usedRows.add(i);
    usedCols.add(j);
  }
  return results;
}
function findBestMatchWithScore(text, candidates) {
  let best = candidates[0] || text;
  let bestScore = 0;
  candidates.forEach(c => {
    const score = similarity(text, c);
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  });
  return { best, score: bestScore };
}
function isDamageForcedByKeyword(name = '') {
  const n = String(name).replace(/\s+/g, '');
  if (!n) return false;
  return n.includes('피해') && n.includes('량');
}


/* =========================================================
 * 숫자/단위 정규화 유틸 (OCR 수치 처리)
 * ======================================================= */
function extractNumberLoose(str) {
  const s = String(str || '');
  let m = s.match(/[0-9][0-9,]*\.?[0-9]*/);
  if (m) return m[0];
  const fixed = fixDigitLookalikes(s);
  m = fixed.match(/[0-9][0-9,]*\.?[0-9]*/);
  return m ? m[0] : '';
}
function normalizeNumericString(s) {
  const str = (s || '').replace(/\s/g, '');
  if (!str.includes('.') && (str.match(/,/g) || []).length === 1) {
    const m = str.match(/^(\d+),(\d{1,2})(?!\d)/);
    if (m) return `${m[1]}.${m[2]}`;
  }
  const dotCount = (str.match(/\./g) || []).length;
  const commaCount = (str.match(/,/g) || []).length;
  if (dotCount > 1 && commaCount === 0) {
    const last = str.lastIndexOf('.');
    const intPart = str.slice(0, last).replace(/\./g, '');
    const fracPart = str.slice(last + 1).replace(/\./g, '');
    return intPart + (fracPart ? '.' + fracPart : '');
  }
  if (commaCount > 1 && dotCount === 0) return str.replace(/,/g, '');
  if (dotCount >= 1 && commaCount >= 1) {
    const lastDot = str.lastIndexOf('.');
    const lastComma = str.lastIndexOf(',');
    const decIdx = Math.max(lastDot, lastComma);
    const intPart = str.slice(0, decIdx).replace(/[.,]/g, '');
    const fracPart = str.slice(decIdx + 1).replace(/[.,]/g, '');
    return intPart + (fracPart ? '.' + fracPart : '');
  }
  if (commaCount === 1 && dotCount === 0) return str.replace(/,/g, '');
  return str;
}
function normalizePercentNumber(numStr) {
  const s = String(numStr || '').replace(/\s/g, '');
  if (!s) return s;
  if (s.includes('.')) return s;
  if (s.length > 2) return s.slice(0, -2) + '.' + s.slice(-2);
  return s;
}
function normalizeFixed4_2(str) {
  const digits = String(str || '').replace(/[^0-9]/g, '');
  if (digits.length < 3) return str || '';
  const d = digits.slice(0, 6);
  const intLen = Math.min(Math.max(d.length - 2, 1), 4);
  const intPart = d.slice(0, intLen);
  const fracPart = d.slice(intLen, intLen + 2).padEnd(2, '0');
  return `${intPart}.${fracPart}`;
}
function parseRawValueStrict(str) {
  const src = String(str || '');
  const hadPercent = /[%％]/.test(src);
  let nStr = extractNumberLoose(src);
  if (hadPercent) nStr = normalizePercentNumber(nStr);
  nStr = normalizeNumericString(nStr);
  const n = parseFloat(nStr);
  return Number.isFinite(n) ? n : 0;
}
function formatValueWithUnit(valueStr, traitName) {
  const raw = fixDigitLookalikes(String(valueStr || '').trim());
  const numericCore = extractNumberLoose(raw);
  const numericNorm = normalizeNumericString(numericCore);
  const mm = numericNorm.match(NUM_ONLY_RE);
  if (!mm) {
    if (isPercentTraitName(traitName)) return '0%';
    if (isCountTraitName(traitName)) return '0회';
    return '0';
  }
  const intPart = mm[1];
  const fracFull = mm[2] || '';
  const hadFrac = fracFull.length > 0;
  const overflow = fracFull.slice(2);
  const shown = toTwoDecimalsTruncate(`${intPart}${hadFrac ? '.' + fracFull : ''}`);
  const numericShown = parseFloat(shown.replace(/,/g, '')) || 0;
  const isZero = numericShown === 0;
  const origUnit = (raw.match(/([만억조회%])$/) || [])[1] || '';
  if (isZero) {
    if (isPercentTraitName(traitName)) return '0%';
    if (isCountTraitName(traitName)) return '0회';
    return '0';
  }
  if (isDamageForcedByKeyword(traitName) || isDamageLikeName(traitName)) {
    const { cands, fallback } = damageUnitCandidates(traitName);
    const inferred = inferUnitGeneric(overflow, cands, fallback, 0.60);
    const origUnitDamage = /[만억조]/.test(origUnit) ? origUnit : '';
    let finalUnit = origUnitDamage || inferred || fallback || '억';
    if (finalUnit === '%') {
      finalUnit = fallback || '억';
    }
    const isReceived = /받은피해량/.test(String(traitName).replace(/\s+/g, ''));
    const looksBareUpTo4Digits =
      /^([0-9]{1,4})(?:\.0{1,2})?$/.test(numericNorm) && !origUnit;
    if (isReceived && looksBareUpTo4Digits) {
      return `${shown}`;
    }
    return `${shown}${finalUnit}`;
  }
  if (isCountTraitName(traitName)) return `${intPart}회`;
  if (isMuleoghName(traitName)) {
    return hadFrac ? `${shown}만` : shown;
  }
  if (isPercentTraitName(traitName)) {
    const inferred = inferUnitGeneric(overflow, ['%'], '%', 0.6);
    const unit = origUnit || inferred || '%';
    return `${shown}${unit}`;
  }
  return shown;
}


/* =========================================================
 * 피해/단위 후검증 유틸 (DPS vs 총합 등)
 * ======================================================= */
function nameKey(s) { return String(s || '').replace(/\s+/g, '').toLowerCase(); }
function isTotalDamageName(name) {
  const n = nameKey(name);
  return n.includes('피해') && n.includes('량') && !n.includes('초당') && !n.includes('1분') && !n.includes('받은');
}
function isDpsDamageName(name) {
  const n = nameKey(name);
  return n.includes('초당') && n.includes('피해') && n.includes('량') && !n.includes('1분');
}
function isMinTotalDamageName(name) {
  const n = nameKey(name);
  return n.includes('1분') && n.includes('피해') && n.includes('량') && !n.includes('초당');
}
function isMinDpsDamageName(name) {
  const n = nameKey(name);
  return n.includes('1분') && (n.includes('초당') || n.includes('dps')) && n.includes('피해') && n.includes('량');
}
function nextLowerUnit(u) {
  if (u === '조') return '억';
  if (u === '억') return '만';
  return '만';
}
function setUnitKeepNumber(valueStr, newUnit) {
  const s = String(valueStr || '').trim();
  const m = s.match(/^([0-9][0-9,]*)(?:\.([0-9]+))?/);
  if (!m) return s;
  const numStr = toTwoDecimalsTruncate((m[1] || '') + (m[2] ? '.' + m[2] : ''));
  return `${numStr}${newUnit}`;
}
function parseNumOnly(valueStr) {
  const m = String(valueStr || '').trim().match(/^([0-9][0-9,]*)(?:\.([0-9]+))?/);
  if (!m) return null;
  const numStr = toTwoDecimalsTruncate((m[1] || '') + (m[2] ? '.' + m[2] : ''));
  return parseFloat(numStr.replace(/,/g, ''));
}
function detectUnit(valueStr) {
  return (String(valueStr || '').trim().match(/([만억조회%])$/) || [])[1] || '';
}
function enforceDamageRateUnits(mainTraits, addTraits) {
  const all = [...(mainTraits || []), ...(addTraits || [])];
  const total = all.find(t => isTotalDamageName(t.trait));
  const dps = all.find(t => isDpsDamageName(t.trait));
  const minTot = all.find(t => isMinTotalDamageName(t.trait));
  const minDps = all.find(t => isMinDpsDamageName(t.trait));
  const defaultDamageUnit = '억';
  if (total && dps) {
    const totalNum = parseNumOnly(total.value);
    const dpsNum = parseNumOnly(dps.value);
    const totalUnit = detectUnit(total.value) || defaultDamageUnit;
    if (Number.isFinite(totalNum) && Number.isFinite(dpsNum)) {
      if (dpsNum > totalNum) {
        dps.value = setUnitKeepNumber(dps.value, nextLowerUnit(totalUnit));
      }
    }
  }
  if (minTot && minDps) {
    const totalNum = parseNumOnly(minTot.value);
    const dpsNum = parseNumOnly(minDps.value);
    const totalUnit = detectUnit(minTot.value) || defaultDamageUnit;
    if (Number.isFinite(totalNum) && Number.isFinite(dpsNum)) {
      if (dpsNum > totalNum) {
        minDps.value = setUnitKeepNumber(minDps.value, nextLowerUnit(totalUnit));
      }
    }
  }
  return { main: mainTraits, add: addTraits };
}

/* =========================================================
 * 레이드명 보정/스코어 유틸 (한글·기호 변형 대응)
 * ======================================================= */
const FULLWIDTH_DIGITS_RE = /[０-９]/g;
const full2halfDigits = s =>
  String(s || '').replace(FULLWIDTH_DIGITS_RE, ch =>
    String.fromCharCode(ch.charCodeAt(0) - 0xFF10 + 0x30)
  );
function normalizeRaidNameForMatch(s) {
  let t = full2halfDigits(String(s || '')).trim();
  t = t.replace(/[：﹕꞉··⋅・]/g, ':');
  t = t.replace(/^(\d+)\s*[%‰]\s*:/, '$1막 :');
  // 관문 앞 1 오인식 보정(한글 ㅣ, 로마 Ⅰ, 각종 세로바 포함)
  t = t.replace(/([lI|!ｌＩ])(?=\s*관문)/g, '1');
  // 관문 앞 1 오인식 보정(한글 ㅣ, 로마 Ⅰ, 각종 세로바 포함) + 사이에 문장부호 허용
  // 예: "ㅣ。관문", "Ⅰ 관문", "|, 관문" 등
  t = t.replace(
    /([lI|!ｌＩㅣⅠ❘︱｜│])[.\-_,·⋅・·:;，、。／/\\|\s]*(?=관문)/g,
    '1'
  );
  // ‘관문’만 있고 숫자 없으면 1관문으로 보정
  if (/관문/.test(t) && !/\d+\s*관문/.test(t)) {
    t = t.replace(/관문/g, '1관문');
  }
  t = t.replace(/\s+/g, ' ').trim();
  return t;
}
function extractStageAndGate(s) {
  const t = normalizeRaidNameForMatch(s).replace(/\s+/g, '');
  let stage = null, gate = null;
  const mStage = t.match(/^(\d+)막/);
  if (mStage) stage = parseInt(mStage[1], 10);
  // 숫자와 '관문' 사이에 문장부호(.,、，。 / 등) 허용
  const mGate = t.match(/(\d+)[.\-_,·⋅・·:;，、。／/\\|\s]*관문/);
  if (mGate) gate = parseInt(mGate[1], 10);
  return { stage, gate };
}


/* =========================================================
 * 숫자 보정/이름 분류/단위 추론 유틸
 * ======================================================= */
function fixDigitLookalikes(s) {
  if (!s) return s;
  let t = String(s);
  t = t.replace(/\bpi\b/gi, '2');
  t = t.replace(/[Oo]/g, '0');
  t = t.replace(/[Il]/g, '1');   // 영문 I,l → 1
  t = t.replace(/ㅣ/g, '1');     // 한글 'ㅣ' → 1
  t = t.replace(/[Zz]/g, '2');
  t = t.replace(/[Ss]/g, '5');
  t = t.replace(/[gqGQ]/g, '9');
  return t;
}
function toTwoDecimalsTruncate(numStr) {
  const s = String(numStr || '');
  const m = s.match(/^([0-9][0-9,]*)(?:\.([0-9]+))?$/);
  if (!m) return s;
  const intPart = m[1];
  const frac = (m[2] || '');
  const frac2 = frac.slice(0, 2).padEnd(2, '0');
  return frac ? `${intPart}.${frac2}` : intPart;
}
function isCountTraitName(name = '') {
  const n = String(name).replace(/\s+/g, '');
  return n.includes('횟수');
}
function isMuleoghName(name = '') {
  const n = String(name).replace(/\s+/g, '');
  return n.includes('무력화');
}
function isPercentTraitName(name = '') {
  const n = String(name).replace(/\s+/g, '');
  if (n.includes('받은피해량')) return false;
  return /[율률]|가동률|유효율|적중률/.test(n);
}
function isDamageLikeName(name = '') {
  const n = String(name).replace(/\s+/g, '');
  return /(피해.*량|초당피해량|1분피해량|받은피해량)/.test(n);
}
function damageUnitCandidates(name = '') {
  const n = String(name).replace(/\s+/g, '');
  if (n.includes('받은피해량')) {
    return { cands: ['만', '억', '조'], fallback: '만' };
  }
  return { cands: ['억', '만', '조'], fallback: '억' };
}
function inferUnitGeneric(overflow, candidates, fallback, acceptRatio = 0.6) {
  const src = String(overflow || '').trim();
  if (!src) return null;
  let best = null;
  let bestDist = Infinity;
  for (const unit of candidates) {
    const d = levenshtein(src, unit);
    if (d < bestDist) { bestDist = d; best = unit; }
  }
  const denom = Math.max(src.length, (best || '').length, 1);
  const ratio = bestDist / denom;
  if (ratio <= acceptRatio) return best;
  return fallback ?? null;
}
const NUM_ONLY_RE = /^([0-9][0-9,]*)(?:\.([0-9]+))?$/;


/* =========================================================
 * 공통 헬퍼 (문자/시간/타임스탬프/병합)
 * ======================================================= */

function normalizeTimestamp(raw) {
  const digits = (raw || '').replace(/[^0-9]/g, ' ').trim();
  const parts = digits.split(/\s+/).map(n => parseInt(n, 10));
  if (parts.length >= 6) {
    const [y, mo, da, hh, mi, ss] = parts;
    const z2 = v => String(v).padStart(2, '0');
    return `${y}.${z2(mo)}.${z2(da)} ${z2(hh)}:${z2(mi)}:${z2(ss)}`;
  }
  return raw || '';
}
function compact(s) { return (s || '').replace(/\s+/g, ''); }
// 단순 비교용 정규화 유틸: 공백 제거 + 소문자
function normForCompare(s) {
  return String(s || '').replace(/\s+/g, '').toLowerCase();
}

/* =========================================================
 * 직업/각인 메타 조회 (ORDER/개수 등)
 * ======================================================= */
function getEngravingMeta(jobName, engravingName) {
  if (!jobName || !engravingName) return null;
  const job = JOB_CONFIG[jobName];
  if (!job || !Array.isArray(job.engravings)) return null;
  return job.engravings.find(e => e?.name === engravingName) || null;
}
function getJobConfig(job, engravingName) {
  const base = JOB_CONFIG._base || {};
  const jobCfg = JOB_CONFIG[job] || {};
  const eng = getEngravingMeta(job, engravingName);
  const role = eng?.role || null;
  const roleTraits = role ? (ROLE_COMMON_TRAITS[role] || []) : [];
  const traitNames = Array.from(
    new Set([
      ...(base.commonTraits || []),
      ...roleTraits,
      ...((jobCfg.addTraits) || []),
      ...((eng?.addTraits) || []),
    ])
  );
  const mainCount =
    Number.isFinite(jobCfg.mainCount) ? jobCfg.mainCount :
      Number.isFinite(base.mainCountDefault) ? base.mainCountDefault : 4;
  const addCount =
    Number.isFinite(eng?.addCount) ? eng.addCount :
      Number.isFinite(jobCfg.addCount) ? jobCfg.addCount :
        Number.isFinite(base.addCountDefault) ? base.addCountDefault : 13;
  const engravings = (jobCfg.engravings || []).map(e => e?.name).filter(Boolean);
  return { traitNames, mainCount, addCount, engravings, role };
}


/* =========================================================
 * 평균/정렬/집계 유틸
 * ======================================================= */
function parseRawValue(str, traitName) {
  const n = parseRawValueStrict(str, traitName);
  if (!Number.isFinite(n)) return 0;
  const unit = (String(str || '').trim().match(/([만억조%회])$/) || [])[1] || '';
  const needsUnitScaling =
    isDamageLikeName(traitName) ||
    isTotalDamageName?.(traitName) ||
    isDpsDamageName?.(traitName) ||
    isMinTotalDamageName?.(traitName) ||
    isMinDpsDamageName?.(traitName) ||
    isMuleoghName(traitName);
  if (!needsUnitScaling) return n;
  let mul = 1;
  if (unit === '만') mul = 1e4;
  else if (unit === '억') mul = 1e8;
  else if (unit === '조') mul = 1e12;
  return n * mul;
}
// eslint-disable-next-line no-unused-vars
function traitsToMap(main = [], add = []) {
  const map = new Map();
  const put = (t) => {
    if (!t || !t.trait) return;
    const unit = (t.value.match(/[^0-9.,]+$/) || [''])[0];
    map.set(t.trait, {
      trait: t.trait,
      value: t.value,
      unit,
      raw: parseRawValue(t.value, t.trait),
    });
  };
  main.forEach(put);
  add.forEach(put);
  return map;
}
function normalizeTraitName(name) {
  if (!name) return '';
  const n = name.replace(/\s+/g, '').toLowerCase();
  if (n.includes('저스트가드')) return '저스트 가드 성공 횟수';
  if (n === '1분피해량' || n === '1분dps') return '1분 피해량';
  return name;
}
// eslint-disable-next-line no-unused-vars
function groupTraitsByName(records) {
  const bucket = {};
  records.forEach(r => {
    const all = []
      .concat(r.mainTraits || [])
      .concat(r.additionalTraits || []);
    all.forEach(t => {
      const name = normalizeTraitName((t.trait || '').trim());
      if (!name) return;
      const val = String(t.value || '');
      const raw = parseRawValue(val, name);
      const unit = (val.match(/[^0-9.,]+$/) || [''])[0];
      if (!bucket[name]) bucket[name] = [];
      bucket[name].push({ raw, unit });
    });
  });
  return bucket;
}
// eslint-disable-next-line no-unused-vars
function formatByUnitForDisplay(raw, traitName) {
  if (!Number.isFinite(raw)) return '';
  const isCount = isCountTraitName(traitName);
  const isRate = isPercentTraitName(traitName);
  if (isCount) {
    let s = raw.toFixed(2);
    s = s.replace(/\.00$/, '');
    s = s.replace(/(\.\d)0$/, '$1');
    return s + '회';
  }
  if (isRate) {
    let s = raw.toFixed(2) + '%';
    s = s.replace(/\.00%$/, '%');
    return s;
  }
  const isMagnitudeBased =
    isMuleoghName(traitName) ||
    isDamageLikeName(traitName) ||
    (typeof isTotalDamageName === 'function' && isTotalDamageName(traitName)) ||
    (typeof isDpsDamageName === 'function' && isDpsDamageName(traitName)) ||
    (typeof isMinTotalDamageName === 'function' && isMinTotalDamageName(traitName)) ||
    (typeof isMinDpsDamageName === 'function' && isMinDpsDamageName(traitName));
  if (isMagnitudeBased) {
    let s;
    if (raw >= 1e12) s = (raw / 1e12).toFixed(2) + '조';
    else if (raw >= 1e8) s = (raw / 1e8).toFixed(2) + '억';
    else if (raw >= 1e4) s = (raw / 1e4).toFixed(2) + '만';
    else s = raw.toFixed(2);
    s = s.replace(/\.00(조|억|만)?$/, '$1');
    return s;
  }
  let s = raw.toFixed(2);
  s = s.replace(/\.00$/, '');
  return s;
}
/* =========================================================
 * 한글 OCR 보조 유틸(자모/초성 기반 매칭)
 * ======================================================= */
const KEEP_HANGUL = /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7A3\uA960-\uA97F\uD7B0-\uD7FF]/g;
function filterByCandidateChars(ocr, candidate) {
  const set = new Set((candidate.match(KEEP_HANGUL) || []));
  return (ocr.match(KEEP_HANGUL) || []).filter(ch => set.has(ch)).join('');
}
function lcsRatio(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return n ? dp[m][n] / n : 0;
}
function choseongSeq(s) {
  const CHO = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
  return Array.from(s).map(ch => {
    const code = ch.charCodeAt(0);
    if (code < 0xAC00 || code > 0xD7A3) return ch;
    const idx = code - 0xAC00;
    const cho = Math.floor(idx / 588);
    return CHO[cho];
  }).join('');
}
function jamoSim(a, b) {
  const aa = choseongSeq(a), bb = choseongSeq(b);
  return lcsRatio(aa, bb);
}
function bestEngravingMatch(ocrRaw, candidates) {
  const ocr = (ocrRaw || '').match(KEEP_HANGUL)?.join('') ?? '';
  let best = candidates[0] || '', bestScore = 0;
  for (const cand of candidates) {
    const filtered = filterByCandidateChars(ocr, cand);
    const sLCS = lcsRatio(filtered, cand);
    const sJamo = jamoSim(filtered, cand);
    const sLev = similarity(filtered, cand);
    const score = 0.55 * sLCS + 0.30 * sJamo + 0.15 * sLev;
    if (score > bestScore) { bestScore = score; best = cand; }
  }
  const accept = bestScore >= 0.5;
  return { best: accept ? best : '', score: bestScore };
}



/* ---------- 공격대 평균(딜러만) 계산 유틸 ---------- */
function timeToSecMMSS(s = '') {
  const p = String(s).split(':').map(n => parseInt(n, 10) || 0);
  if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
  if (p.length === 2) return p[0] * 60 + p[1];
  return p[0] || 0;
}
function secToMMSS(sec = 0) {
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
function isDealerRole(job, engraving, JOB_CONFIG) {
  const meta = (JOB_CONFIG[job]?.engravings || []).find(e => e?.name === engraving);
  return (meta?.role || '') !== 'supporter';
}
function extractDamageFromRecord(rec, parseRawValue) {
  const all = []
    .concat(rec?.payload?.mainTraits || [])
    .concat(rec?.payload?.additionalTraits || []);
  const findVal = (pred) => {
    const t = all.find(x => pred(x?.trait || ''));
    return parseRawValue(t?.value || '0', t?.trait || '');
  };
  const total = findVal(isTotalDamageName);
  const dps = findVal(isDpsDamageName);
  const sec = timeToSecMMSS(rec?.payload?.battleTime || '');
  return { total, dps, sec };
}
function computeRaidAverageFrom(records, JOB_CONFIG, parseRawValue) {
  const dealers = (records || []).filter(r =>
    isDealerRole(r?.job || r?.payload?.job || '', r?.engraving || r?.payload?.engraving || '', JOB_CONFIG)
  );
  const n = dealers.length;
  if (!n) return null;

  let sumTotal = 0, sumDps = 0, sumSec = 0;
  for (const r of dealers) {
    const { total, dps, sec } = extractDamageFromRecord(r, parseRawValue);
    sumTotal += total;
    sumDps += dps;
    sumSec += sec;
  }
  return {
    dealerCount: n,
    avgTotal: sumTotal / n,
    avgDps: sumDps / n,
    avgSec: Math.round(sumSec / n),
  };
}



/* =========================================================
 * 내부 소형 UI 컴포넌트 (FAB/BottomSheet)
 * ======================================================= */
function MobileSavedRaidsFab({ onOpen, shown }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={[
        "lg:hidden fixed bottom-4 right-4 z-40 rounded-full shadow-lg",
        "bg-indigo-600 text-white px-4 py-3 text-sm",
        "transition-all",
        "transform will-change-transform",
        shown
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-2 pointer-events-none"
      ].join(" ")}
      aria-label="공격대 기록 열기"
    >
      공격대 기록
    </button>
  );
}
function MobileBottomSheet({ open, onClose, children }) {
  return (
    <div className={`lg:hidden fixed inset-0 z-40 ${open ? '' : 'pointer-events-none'}`}>
      <div
        className={[
          "absolute inset-0 bg-black/40",
          "transition-opacity duration-300 ease-out",
          open ? "opacity-100" : "opacity-0"
        ].join(' ')}
        onClick={onClose}
      />
      <div
        className={[
          "absolute inset-x-0 bottom-0 h-[65vh] rounded-t-2xl bg-white dark:bg-zinc-900 shadow-2xl flex flex-col flex-1 basis-[0px] min-h-0",
          "transition-transform will-change-transform",
          open ? "translate-y-0" : "translate-y-full"
        ].join(' ')}
      >
        <div className="mx-auto mt-2 mb-1 h-1.5 w-10 rounded-full bg-zinc-300 dark:bg-zinc-700" />
        <div className="p-3 overflow-y-auto flex flex-col flex-1 basis-[0px] min-h-0 ">{children}</div>
      </div>
    </div>
  );
}


/* =========================================================
 * 메인 컴포넌트
 * ======================================================= */
export default function PartyPage() {
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const navigate = useNavigate();

  const { code = "" } = useParams();
  const { user: authUser } = useAuth();
  const uid = authUser?.uid;

  const [stream, setStream] = useState(null);
  const [profileInfo, setProfileInfo] = useState({ job: '', jobEngraving: '', level: '', power: '' });
  const [battleMainData, setBattleMainData] = useState({});
  const [step, setStep] = useState('idle');

  // eslint-disable-next-line no-unused-vars
  const [viewMode, setViewMode] = useState('live');

  const [party, setParty] = useState(null);
  const [me, setMe] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);

  const [progressByStep, setProgressByStep] = useState({
    profile: { done: 0, total: 0 },
    battleMain: { done: 0, total: 0 },
    battleMain2: { done: 0, total: 0 },
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [memo, setMemo] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [raidName, setRaidName] = useState("");
  const partialRef = useRef(null);
  const [bigView, setBigView] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [mobileSavedOpen, setMobileSavedOpen] = useState(false);
  const [raidAvgAll, setRaidAvgAll] = useState([]);
  const [raidAvgCurrent, setRaidAvgCurrent] = useState(null);
  const [raidAvgSnapshots, setRaidAvgSnapshots] = useState([]);
  const [raidAvgModalOpen, setRaidAvgModalOpen] = useState(false);

  const bootHold = useMinDelay(loadingAll, 1000);

  const [fabShown, setFabShown] = useState(true);
  const forceVisibleRef = useRef(0);
  const hideTimerRef = useRef(null);
  const HIDE_DELAY = 5000;
  const END_THRESHOLD = 56;

  const isAtEnd = () => {
    const se = document.scrollingElement || document.documentElement || document.body;
    const viewportBottom = (se.scrollTop || 0) + (window.innerHeight || 0);
    const pageBottom = (se.scrollHeight || 0);
    return viewportBottom >= pageBottom - END_THRESHOLD;
  };

  // 관문 관리 상태
  const [gateName, setGateName] = useState(""); // 현재 파티 “기록실 관문” (full name)
  const isLeader = useMemo(() => !!party && party.ownerUid === uid, [party, uid]);

  // 파티 생성시 저장된 레이드명은 “관문 제외 베이스 레이드명”
  const baseRaidName = party?.raidName || "";
  const gateOptions = useMemo(() => {
    const opts = getGateOptions(baseRaidName);
    return opts.map(g => {
      const n = getGateNumber(g);
      return n ? `${n}관문` : g;
    });
  }, [baseRaidName]);

  // eslint-disable-next-line no-unused-vars
  const [stepBarError, setStepBarError] = useState('');

  // ---- 베이스/관문 원천값 수집
  const raidBaseRaw =
    party?.raidBaseName ??
    party?.raidName ??
    "";

  const gateRawFromParty =
    party?.gateName ??
    party?.selectedGate ??
    party?.gate ??
    "";

  // ✅ 실제 사용 관문: state(gateName) 우선, 없으면 party 필드
  const effectiveGate = gateName || gateRawFromParty;
  const hasConfig = !!raidBaseRaw && !!effectiveGate;

  // 저장 가능 조건
  const canStartCapture = !!raidBaseRaw && !!effectiveGate;
  const startTooltip = "공대장이 관문 설정 후 저장시 화면 공유를 시작할 수 있어요.";

  const [loaInfo, setLoaInfo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!me?.nickname) return;
        const info = await fetchLoaSummary(me.nickname);
        if (!cancelled) setLoaInfo(info);
      } catch {
        if (!cancelled) setLoaInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [me?.nickname]);
  const fmt2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : "");
  const displayJob = (loaInfo?.className || me?.job || profileInfo.job || "");
  const displayEngraving = (loaInfo?.engraving || me?.engraving || profileInfo.jobEngraving || "").trim();
  const displayPower = Number.isFinite(loaInfo?.combatPower) ? fmt2(loaInfo.combatPower) : (me?.power ? fmt2(me.power) : "");
  const displayLevel = Number.isFinite(loaInfo?.itemAvgLevel) ? fmt2(loaInfo.itemAvgLevel) : (me?.level ? fmt2(me.level) : "");

  
  const { addCount: _addCountForUI } = getJobConfig(displayJob || '알수없음', displayEngraving || '');
  const showPage2Progress = _addCountForUI > 14;


  // OCR & 설정 정규화
  const rn = normForCompare(raidName);
  const rb = normForCompare(raidBaseRaw);
  const gg = normForCompare(effectiveGate);

  // eslint-disable-next-line no-unused-vars
  const canSave = hasConfig && !!rn && rn.includes(rb) && rn.includes(gg);

  // battleMain용: OCR 레이드명 vs 공격대 설정(베이스+관문) 불일치 체크 → StepProgressBar에 띄울 에러 세팅
  // PartyPage 컴포넌트 내부
  const checkRaidGateMismatch = useCallback((ocrRaidName) => {
    // 공격대 설정 원천값
    const raidBaseRaw =
      party?.raidBaseName ??
      party?.raidName ??
      "";

    const gateRawFromParty =
      party?.gateName ??
      party?.selectedGate ??
      party?.gate ??
      "";

    const effectiveGate = gateName || gateRawFromParty;
    const hasConfig = !!(raidBaseRaw && effectiveGate);
    if (!hasConfig) return; // 설정 없으면 검사 스킵 (이미 화면공유 버튼에서 막음)

    // 정규화 비교
    const rn = normForCompare(ocrRaidName);
    const rb = normForCompare(raidBaseRaw);
    const gg = normForCompare(effectiveGate);

    const ok = rn && rn.includes(rb) && rn.includes(gg);
    if (!ok) {
      // 인식 실패와 '같은 방식'으로 처리되도록 throw
      throw new Error(
        `공격대 설정된 레이드/관문("${raidBaseRaw} ${effectiveGate}")과 OCR("${ocrRaidName}")이 일치해야 인식할 수 있어요.`
      );
    }
  }, [party, gateName]);


  // 화면 공유 종료 + 상태 초기화
  const resetToStart = () => {
    // 1) 스트림 중지
    try {
      const tracks = stream?.getTracks?.() || [];
      tracks.forEach(t => t.stop());
    } catch {}

    // 2) 상태 초기화
    setStream(null);   // 화면 공유 전 상태
    setStep('');       // 또는 'init'
    setMemo('');       // 메모 초기화(선택)
  };

  // 현재 관문과 일치하는 기록만 (관문 번호 기준 + gateName/targetGate 폴백)
  // eslint-disable-next-line no-unused-vars
  const visibleAnalyses = useMemo(() => {
    const egNum = getGateNumber(effectiveGate); // ex) "1관문" -> 1
    const norm = (s) => String(s || '').replace(/\s+/g, '');

    // 1) 관문 일치 + 게이트 로그(__gate__) 제외
    const filtered = (analyses || []).filter((a) => {
      if (a?.payload?.type === '__gate__') return false;
      // 스냅샷/게이트 로그는 목록에서 제외
      if (a?.payload?.type === '__gate__' || a?.payload?.type === '__avg__') return false;
      if (!effectiveGate) return true;

      const g = getRecordGate(a);
      const agNum = getGateNumber(g);
      if (egNum && agNum) return egNum === agNum;
      return norm(g) === norm(effectiveGate);
    });

    // 2) 최신순 정렬
    filtered.sort((a, b) => recordSeconds(b) - recordSeconds(a));

    // 3) 같은 사람(uid)당 1개만 유지(최신 우선)
    const seen = new Set();
    const uniq = [];
    for (const r of filtered) {
      const key = r?.uid || r?.authorUid || r?.userId || r?.ownerUid || r?.nickname || 'unknown';
      if (seen.has(key)) continue;
      seen.add(key);
      uniq.push(r);
    }
    return uniq;
  }, [analyses, effectiveGate]);


  /* ---- 데이터 로드 ---- */
const loadAll = useCallback(async () => {
  if (!uid || !code) {
    // 불러올 조건이 아니면 로더 끄고 종료
    setLoadingAll(false);
    return;
  }
  setLoadingAll(true);
  setError("");
  try {
    const { party: p, me: m } = await fetchPartyAndMe(code, uid);
    setParty(p); 
    setMe(m);

    // TTL 보정: 오너 + createdAt 있고 expireAt 없을 때만
    try {
      const isOwner = !!p && p.ownerUid === uid;
      if (isOwner && p?.createdAt && !p?.expireAt) {
        await ensurePartyExpireAt(code, p.createdAt);
      }
    } catch (e) {
      console.warn("ensurePartyExpireAt failed:", e);
    }

    // 일반 기록(__rec__) 목록
    const listRaw = await fetchPartyRecords(code, { limit: 200 });
    const list = (listRaw || []).map(r => ({
      id:
        r.id ??
        r.docId ??
        r.key ??
        `${r.uid || ""}-${r.timestamp || ""}-${Math.random().toString(36).slice(2, 8)}`,
      ...r,
    }));
    setAnalyses(list);

    // 게이트명 결정: party.meta → gate_logs → (레거시) analyses
    let curGate = p?.gateName || "";            // ✅ 추가 read 없이 해결
    if (!curGate) {
      const g2 = await fetchLatestGateLog(code); // parties/{code}/gate_logs 최신 1건
      if (g2?.gateName) {
        curGate = g2.gateName;
      } else {
        const g1 = await fetchLatestGateLog(code); // (레거시) analyses의 __gate__
        curGate = g1?.payload?.gateName || "";
      }
    }
    if (curGate) {
      setGateName(curGate);
    } else {
      const opts = getGateOptions(p?.raidName || "");
      setGateName(opts?.[0] || "");
    }
  } catch (e) {
    console.error(e);
    setError(e?.message || "공격대 정보를 불러오지 못했습니다.");
  } finally {
    setLoadingAll(false);
  }
}, [code, uid]);



 useEffect(() => {
   if (uid && code) loadAll();
 }, [uid, code, loadAll]);

  // 파티 메타 gateName 실시간 구독 (관문이 바뀌면 즉시 반영)
  useEffect(() => {
    if (!code) return;
    const unsub = subscribePartyGateName(code, (gn) => { if (gn) setGateName(gn); });
    return () => unsub && unsub();
  }, [code]);


  // CHANGED: 평균(__avg__) 실시간 수신은 "누적"으로 관리 (관문 전환 시 이전 관문 스냅샷 보존)
  // 구독: 최-초 1개 고정 + 최신 19개 (총 20개)만 실시간으로 받아옴
  useEffect(() => {
    if (!code) return;

    const unsub = subscribePartyAveragesPinnedFirst(code, (arr) => {
      setRaidAvgAll((prev) => {
        const map = new Map();

        // 1) 기존에 가지고 있던 모든 스냅샷 유지
        for (const d of prev || []) {
          const key = d?.id || d?.docId || `${d?.payload?.gateName}-${d?.createdAt?.seconds || ''}`;
          map.set(key, d);
        }

        // 2) 새로 들어온 스냅샷 병합(동일 id는 갱신)
        for (const d of (Array.isArray(arr) ? arr : [])) {
          const key = d?.id || d?.docId || `${d?.payload?.gateName}-${d?.createdAt?.seconds || ''}`;
          map.set(key, d);
        }

        return Array.from(map.values());
      });
    }, 20);

    return () => unsub && unsub();
  }, [code]);


  // CHANGED: 현재 선택 관문(effectiveGate)에 해당하는 스냅샷만 뽑아 최신순 정렬하여 표시 상태로 반영
  useEffect(() => {
    const eg = normForCompare(effectiveGate || "");
    const filtered = (raidAvgAll || [])
      .filter(doc => normForCompare(doc?.payload?.gateName || "") === eg)
      .slice() // 안전 복사
      .sort((a, b) => {
        const as = a?.createdAt?.seconds ?? a?.createdAt?.toMillis?.() / 1000 ?? 0;
        const bs = b?.createdAt?.seconds ?? b?.createdAt?.toMillis?.() / 1000 ?? 0;
        return bs - as;
      });

    setRaidAvgSnapshots(filtered);
    setRaidAvgCurrent(filtered[0] || null);
  }, [raidAvgAll, effectiveGate]);

  useEffect(() => {
    if (!me) return;
    setProfileInfo(prev => ({
      job: me.job || prev.job || '',
      jobEngraving: me.engraving || prev.jobEngraving || '',
      level: prev.level || '',
      power: prev.power || '',
    }));
  }, [me]);

  /* ---- 관문 저장(공대장만) ---- */
  const saveGateSelection = useCallback(async () => {
    if (!isLeader) return;
    if (!gateName) {
      alert("관문을 선택해주세요.");
      return;
    }
    try {
      await savePartyGateSelection({
        code,
        uid,
        nickname: me?.nickname || "",
        job: me?.job || "",
        engraving: me?.engraving || "",
        gateName,
        // 레거시 클라가 있을 수 있으니, 당분간 분석 컬렉션에도 함께 기록
        alsoWriteLegacyAnalysis: true,
      });
      await loadAll();
      alert("관문 설정을 저장했습니다.");
    } catch (e) {
      console.error(e);
      alert("관문 저장 실패: " + (e?.message || e));
    }
  }, [isLeader, gateName, code, uid, me, loadAll]);

  useEffect(() => {
    setFabShown(true);
    hideTimerRef.current && clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setFabShown(!isAtEnd());
    }, 1400);

    const recalcByScroll = () => {
      const forced = Date.now() < forceVisibleRef.current;
      if (forced) return;
      setFabShown(!isAtEnd());
    };

    const onUserInteract = () => {
      setFabShown(true);
      forceVisibleRef.current = Date.now() + HIDE_DELAY;

      hideTimerRef.current && clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        forceVisibleRef.current = 0;
        setFabShown(!isAtEnd());
      }, HIDE_DELAY);
    };

    window.addEventListener('scroll', recalcByScroll, { passive: true });
    window.addEventListener('pointerdown', onUserInteract, { passive: true });
    window.addEventListener('touchstart', onUserInteract, { passive: true });
    window.addEventListener('mousedown', onUserInteract, { passive: true });

    return () => {
      window.removeEventListener('scroll', recalcByScroll);
      window.removeEventListener('pointerdown', onUserInteract);
      window.removeEventListener('touchstart', onUserInteract);
      window.removeEventListener('mousedown', onUserInteract);
      hideTimerRef.current && clearTimeout(hideTimerRef.current);
    };
  }, []);

  // eslint-disable-next-line no-unused-vars
  const [savedRaids, setSavedRaids] = useState(() => {
    try {
      const saved = localStorage.getItem('savedRaids');
      const parsed = saved ? JSON.parse(saved) : [];
      return parsed.map(r => ({ ...r, _local: true, source: 'local' }));
    } catch {
      return [];
    }
  });

  // eslint-disable-next-line no-unused-vars
  const formatYMD = (ts) => {
    const s = String(ts || '');
    const m = s.replace(/[^\d]/g, ' ').trim().split(/\s+/);
    if (m.length < 3) return '';
    const [y, mo, da] = m;
    const z2 = v => String(v).padStart(2, '0');
    return `${y}-${z2(mo)}-${z2(da)}`;
  };

  const [selectedIds, setSelectedIds] = useState([]);


  /* ---------- 진행률 상태 도우미 ---------- */
  function setStepPlan(stepKey, total) {
    setProgressByStep(prev => ({
      ...prev,
      [stepKey]: { done: 0, total: Math.max(total, 0) }
    }));
    setLoading(true);
    setError(null);
  }
  function bumpStepProgress(stepKey, inc = 1) {
    setProgressByStep(prev => {
      const cur = prev[stepKey] || { done: 0, total: 0 };
      const nextDone = Math.min(cur.total, cur.done + inc);
      return { ...prev, [stepKey]: { ...cur, done: nextDone } };
    });
  }
  function getStepPercent(stepKey) {
    const { done, total } = progressByStep[stepKey] || { done: 0, total: 0 };
    return total ? Math.round((done / total) * 100) : 0;
  }

  /* ---------- 비디오/컨텐츠 좌표 변환 ---------- */
  const CONTENT_BASE_W = 1920;
  const CONTENT_BASE_H = 1080;
  const video = videoRef.current;
  // 컨텐츠는 좌우 1px, 아래 0px, 위는 가변 (stable)
  const getFrame = useCallback((videoEl) => {
    const LEFT = 1;
    const RIGHT = 1;
    const BOTTOM = 0;
    const contentH = ((videoEl.videoWidth - (LEFT + RIGHT)) / 16) * 9;
    const TOP = Math.max(0, videoEl.videoHeight - contentH - BOTTOM);
    return { LEFT, RIGHT, BOTTOM, TOP };
  }, []);
  function roiFullToContent(r) {
    return { x: r.x, y: r.y, w: r.w, h: r.h };
  }
  const getContentGeom = useCallback((videoEl) => {
    const vw = videoEl.videoWidth, vh = videoEl.videoHeight;
    const FRAME = getFrame(videoEl);
    const cw = Math.max(0, vw - (FRAME.LEFT + FRAME.RIGHT));
    const ch = Math.max(0, vh - (FRAME.TOP + FRAME.BOTTOM));
    const scaleX = cw / CONTENT_BASE_W; // 1920 → 실제 컨텐츠 폭
    const scaleY = ch / CONTENT_BASE_H; // 1080 → 실제 컨텐츠 높이
    return { scaleX, scaleY, ox: FRAME.LEFT, oy: FRAME.TOP, FRAME };
  }, [getFrame, CONTENT_BASE_W, CONTENT_BASE_H]);
  function getVideoElementScale(videoEl) {
    if (!videoEl) return { sx: 1, sy: 1 };
    const sx = (videoEl.clientWidth || 1) / (videoEl.videoWidth || 1);
    const sy = (videoEl.clientHeight || 1) / (videoEl.videoHeight || 1);
    return { sx, sy };
  }
  const collectVisibleROIs = useCallback((curStep, job) => {
    const list = [];
    const engr = (battleMainData.jobEngraving || displayEngraving || '');
    if (curStep === 'profile') {
      ['job', 'jobEngraving', 'level', 'power'].forEach(k => list.push({ key: k, r: ROI[k], kind: 'profile' }));
    } else if (curStep === 'battleMain') {
      ['raidName', 'battleTime', 'timestamp'].forEach(k => list.push({ key: k, r: ROI[k], kind: 'meta' }));
      const { mainCount, addCount } = getJobConfig(job || '알수없음', engr);
      for (let i = 1; i <= mainCount; i++) {
        list.push({ key: `mainTrait${i}`, r: ROI[`mainTrait${i}`], kind: 'main' });
        list.push({ key: `mainTrait${i}Value`, r: ROI[`mainTrait${i}Value`], kind: 'value' });
      }
      const firstPageEnd = Math.min(14, addCount);
      for (let i = 1; i <= firstPageEnd; i++) {
        list.push({ key: `addTrait${i}`, r: ROI[`addTrait${i}`], kind: 'add' });
        list.push({ key: `addTrait${i}Value`, r: ROI[`addTrait${i}Value`], kind: 'value' });
      }
    } else if (curStep === 'battleMain2') {
      const { addCount } = getJobConfig(job || '알수없음', engr);
      const map = (addCount <= 16) ? ROI_SECOND_PASS_16 : ROI_SECOND_PASS_18;
      const startIdx = 15, endIdx = Math.min(addCount, 18);
      for (let i = startIdx; i <= endIdx; i++) {
        list.push({ key: `addTrait${i}`, r: map[`addTrait${i}`], kind: 'add' });
        list.push({ key: `addTrait${i}Value`, r: map[`addTrait${i}Value`], kind: 'value' });
      }
    } else if (curStep === 'done') {
      return collectVisibleROIs('battleMain', job);
    }
    return list.filter(v => v.r);
  }, [battleMainData.jobEngraving, displayEngraving]);
  function roundedRectPath(ctx, x, y, w, h, r = 1) {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
    ctx.lineTo(x + rr, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
    ctx.lineTo(x, y + rr);
    ctx.quadraticCurveTo(x, y, x + rr, y);
    ctx.closePath();
  }
  function highlightFill(kind) {
    switch (kind) {
      case 'profile': return 'rgba(255, 215, 0, 0.18)';
      case 'meta': return 'rgba(255, 165, 0, 0.18)';
      case 'main': return 'rgba(46, 204, 113, 0.18)';
      case 'add': return 'rgba(155, 89, 182, 0.18)';
      case 'value': return 'rgba(231, 76, 60, 0.18)';
      default: return 'rgba(255, 255, 0, 0.18)';
    }
  }

  /* ---------- 바이너리 팽창(얇은 획 보정) ---------- */
  function dilateOnce(gd, canvas) {
    let dark = 0, light = 0;
    for (let i = 0; i < gd.length; i += 4) {
      if (gd[i] < 128) dark++;
      else light++;
    }
    const inkVal = (light < dark) ? 255 : 0;
    const W = canvas.width, H = canvas.height;
    const src = new Uint8ClampedArray(gd);
    const idx = (x, y) => (y * W + x) * 4;

    for (let y = 0; y < H - 1; y++) {
      for (let x = 0; x < W - 1; x++) {
        const hasInk =
          src[idx(x, y)] === inkVal ||
          src[idx(x + 1, y)] === inkVal ||
          src[idx(x, y + 1)] === inkVal ||
          src[idx(x + 1, y + 1)] === inkVal;

        const o = idx(x, y);
        const v = hasInk ? inkVal : (inkVal ^ 255);
        gd[o] = gd[o + 1] = gd[o + 2] = v;
        gd[o + 3] = 255;
      }
    }
  }

  /* ---------- 하이라이트/디버그 오버레이 ---------- */
  const drawOverlay = useCallback(() => {
    const video = videoRef.current;
    const canvas = overlayRef.current;
    if (!canvas) return;
    const targetW = video ? (video.clientWidth || 1) : (canvas.clientWidth || 1);
    const targetH = video ? (video.clientHeight || 1) : (canvas.clientHeight || 1);
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
    }
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!video || !video.videoWidth || !video.videoHeight) return;
    if (!showOverlay) return;
    const w = video.clientWidth || 1;
    const h = video.clientHeight || 1;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    ctx.lineWidth = 2;
    const { scaleX, scaleY, ox, oy, FRAME } = getContentGeom(video);
    const { sx: ex, sy: ey } = getVideoElementScale(video);
    const job = displayJob || '알수없음';
    const list = collectVisibleROIs(step, job);
    list.forEach(({ r, kind }) => {
      const rc = roiFullToContent(r);
      const x_v = ox + rc.x * scaleX;
      const y_v = oy + rc.y * scaleY;
      const w_v = rc.w * scaleX;
      const h_v = rc.h * scaleY;
      const x = Math.round(x_v * ex);
      const y = Math.round(y_v * ey);
      const ww = Math.round(w_v * ex);
      const hh = Math.round(h_v * ey);
      ctx.save();
      ctx.fillStyle = highlightFill(kind);
      roundedRectPath(ctx, x, y, ww, hh, 3);
      ctx.fill();
      ctx.restore();
    });
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const cw = Math.max(0, vw - (FRAME.LEFT + FRAME.RIGHT));
    const ch = Math.max(0, vh - (FRAME.TOP + FRAME.BOTTOM));
    const lines = [
      `Video:   ${vw} × ${vh}`,
      `Content: ${Math.round(cw)} × ${Math.round(ch)}`,
      `Client:  ${w} × ${h}`,
    ];
    ctx.save();
    ctx.font = '12px sans-serif';
    const pad = 6;
    const lineH = 16;
    const boxW = Math.max(...lines.map(t => ctx.measureText(t).width)) + pad * 2;
    const boxH = lineH * lines.length + pad * 2;
    const boxX = 8;
    const boxY = 8;
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(boxX, boxY, boxW, boxH);
    ctx.fillStyle = '#fff';
    lines.forEach((t, i) => {
      ctx.fillText(t, boxX + pad, boxY + pad + lineH * (i + 0.8));
    });
    ctx.restore();
  }, [step, showOverlay, collectVisibleROIs, getContentGeom, displayJob]);
  /* ---------- ROI 캡처(일반/특수) ---------- */
  function captureROIBase64(roi, special = false) {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) return null;
    const { scaleX, scaleY, ox, oy } = getContentGeom(video);
    const rc = roiFullToContent(roi);
    const sx = Math.round(ox + rc.x * scaleX);
    const sy = Math.round(oy + rc.y * scaleY);
    const sw = Math.round(rc.w * scaleX);
    const sh = Math.round(rc.h * scaleY);
    const up = 1;
    const canvas = document.createElement('canvas');
    canvas.width = sw * up;
    canvas.height = sh * up;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(v, sx, sy, sw, sh, 0, 0, sw * up, sh * up);

    if (roi === MYCHAR_ROI) {
      // eslint-disable-next-line no-unused-vars
      const clamp = (v, lo = 0, hi = 255) => (v < lo ? lo : v > hi ? hi : v) | 0;

      const gaussianKernel1D = (sigma) => {
        const s = Math.max(0.8, sigma || 1.0);
        const radius = Math.max(1, Math.floor(s * 3));
        const size = radius * 2 + 1;
        const k = new Float32Array(size);
        const denom = 2 * s * s;
        let sum = 0;
        for (let i = -radius, idx = 0; i <= radius; i++, idx++) {
          const v = Math.exp(-(i * i) / denom);
          k[idx] = v; sum += v;
        }
        for (let i = 0; i < size; i++) k[i] /= sum;
        return { k, radius };
      };
      const gaussianBlur = (src, w, h, sigma) => {
        const { k, radius } = gaussianKernel1D(sigma);
        const tmp = new Uint8ClampedArray(src.length);
        const out = new Uint8ClampedArray(src.length);
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            let r = 0, g = 0, b = 0, a = 0;
            for (let t = -radius; t <= radius; t++) {
              const xx = Math.min(w - 1, Math.max(0, x + t));
              const i = (y * w + xx) * 4;
              const kv = k[t + radius];
              r += src[i] * kv; g += src[i + 1] * kv; b += src[i + 2] * kv; a += src[i + 3] * kv;
            }
            const o = (y * w + x) * 4;
            tmp[o] = r; tmp[o + 1] = g; tmp[o + 2] = b; tmp[o + 3] = a;
          }
        }
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            let r = 0, g = 0, b = 0, a = 0;
            for (let t = -radius; t <= radius; t++) {
              const yy = Math.min(h - 1, Math.max(0, y + t));
              const i = (yy * w + x) * 4;
              const kv = k[t + radius];
              r += tmp[i] * kv; g += tmp[i + 1] * kv; b += tmp[i + 2] * kv; a += tmp[i + 3] * kv;
            }
            const o = (y * w + x) * 4;
            out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
          }
        }
        return out;
      };
      const otsu = (hist, total) => {
        let sum = 0;
        for (let t = 0; t < 256; t++) sum += t * hist[t];
        let sumB = 0, wB = 0, varMax = 0, thr = 127;
        for (let t = 0; t < 256; t++) {
          wB += hist[t];
          if (!wB) continue;
          const wF = total - wB; if (!wF) break;
          sumB += t * hist[t];
          const mB = sumB / wB, mF = (sum - sumB) / wF;
          const v = wB * wF * (mB - mF) ** 2;
          if (v > varMax) { varMax = v; thr = t; }
        }
        return thr;
      };
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = img.data;
      const W = canvas.width, H = canvas.height;

      (function levels({ inBlack, inWhite, gamma, outBlack, outWhite }) {
        const invRange = 1 / Math.max(1, (inWhite - inBlack));
        const gammaPow = 1 / Math.max(1e-6, gamma);
        for (let i = 0; i < d.length; i += 4) {
          for (let c = 0; c < 3; c++) {
            let v = d[i + c];
            const t = Math.min(1, Math.max(0, (v - inBlack) * invRange));
            v = outBlack + (outWhite - outBlack) * Math.pow(t, gammaPow);
            d[i + c] = (v < 0 ? 0 : v > 255 ? 255 : v) | 0;
          }
        }
      })({ inBlack: 86, inWhite: 165, gamma: 0.1, outBlack: 7, outWhite: 153 });

      (function channelAdjust({ rGain, gGain, bGain, rOffset, gOffset, bOffset }) {
        for (let i = 0; i < d.length; i += 4) {
          d[i] = (d[i] * rGain + rOffset) | 0;
          d[i + 1] = (d[i + 1] * gGain + gOffset) | 0;
          d[i + 2] = (d[i + 2] * bGain + bOffset) | 0;
        }
      })({ rGain: 0.77, gGain: 1, bGain: 1.73, rOffset: -255, gOffset: 2, bOffset: 0 });

      (function grayscale() {
        for (let i = 0; i < d.length; i += 4) {
          const gray = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          const v = (gray < 0 ? 0 : gray > 255 ? 255 : gray) | 0;
          d[i] = d[i + 1] = d[i + 2] = v;
        }
      })();

      (function brightnessContrast({ brightness, contrast }) {
        const intercept = 128 * (1 - contrast) + brightness;
        for (let i = 0; i < d.length; i += 4) {
          const v = (d[i] * contrast + intercept) | 0;
          const vv = (v < 0 ? 0 : v > 255 ? 255 : v) | 0;
          d[i] = d[i + 1] = d[i + 2] = vv;
          d[i + 3] = 255;
        }
      })({ brightness: 255, contrast: 4 });

      (function invert() {
        for (let i = 0; i < d.length; i += 4) {
          d[i] = 255 - d[i];
          d[i + 1] = 255 - d[i + 1];
          d[i + 2] = 255 - d[i + 2];
        }
      })();

      (function softThreshold({ softness, shift }) {
        const hist = new Uint32Array(256);
        for (let i = 0; i < d.length; i += 4) hist[d[i]]++;
        const total = W * H;
        let thr = otsu(hist, total);
        thr = Math.max(0, Math.min(255, thr + shift));
        for (let i = 0; i < d.length; i += 4) {
          const diff = d[i] - thr;
          let v;
          if (Math.abs(diff) >= softness) {
            v = diff > 0 ? 255 : 0;
          } else {
            v = (((diff + softness) / (2 * softness)) * 255) | 0;
          }
          d[i] = d[i + 1] = d[i + 2] = v;
        }
      })({ softness: 104, shift: -64 });

      const UNSHARP_ENABLED = false;
      if (UNSHARP_ENABLED) {
        (function unsharpMask({ amount, sigma, threshold }) {
          const src = new Uint8ClampedArray(d);
          const blurred = gaussianBlur(src, W, H, sigma);
          for (let i = 0; i < d.length; i += 4) {
            const hr = src[i] - blurred[i];
            const hg = src[i + 1] - blurred[i + 1];
            const hb = src[i + 2] - blurred[i + 2];
            const mag = Math.max(Math.abs(hr), Math.abs(hg), Math.abs(hb));
            if (mag > threshold) {
              d[i] = (src[i] + amount * hr) | 0;
              d[i + 1] = (src[i + 1] + amount * hg) | 0;
              d[i + 2] = (src[i + 2] + amount * hb) | 0;
            } else {
              d[i] = src[i];
              d[i + 1] = src[i + 1];
              d[i + 2] = src[i + 2];
            }
          }
        })({ amount: 1.2, sigma: 1.2, threshold: 2 });
      }

      ctx.putImageData(img, 0, 0);
      return canvas.toDataURL('image/png');
    } else if (special) {
      // eslint-disable-next-line no-unused-vars
      const clamp = (v, lo = 0, hi = 255) => (v < lo ? lo : v > hi ? hi : v) | 0;

      const gaussianKernel1D = (sigma) => {
        const s = Math.max(0.8, sigma || 1.0);
        const radius = Math.max(1, Math.floor(s * 3));
        const size = radius * 2 + 1;
        const k = new Float32Array(size);
        const denom = 2 * s * s;
        let sum = 0;
        for (let i = -radius, idx = 0; i <= radius; i++, idx++) {
          const v = Math.exp(-(i * i) / denom);
          k[idx] = v; sum += v;
        }
        for (let i = 0; i < size; i++) k[i] /= sum;
        return { k, radius };
      };
      const gaussianBlur = (src, w, h, sigma) => {
        const { k, radius } = gaussianKernel1D(sigma);
        const tmp = new Uint8ClampedArray(src.length);
        const out = new Uint8ClampedArray(src.length);
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            let r = 0, g = 0, b = 0, a = 0;
            for (let t = -radius; t <= radius; t++) {
              const xx = Math.min(w - 1, Math.max(0, x + t));
              const i = (y * w + xx) * 4;
              const kv = k[t + radius];
              r += src[i] * kv;
              g += src[i + 1] * kv;
              b += src[i + 2] * kv;
              a += src[i + 3] * kv;
            }
            const o = (y * w + x) * 4;
            tmp[o] = r; tmp[o + 1] = g; tmp[o + 2] = b; tmp[o + 3] = a;
          }
        }
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            let r = 0, g = 0, b = 0, a = 0;
            for (let t = -radius; t <= radius; t++) {
              const yy = Math.min(h - 1, Math.max(0, y + t));
              const i = (yy * w + x) * 4;
              const kv = k[t + radius];
              r += tmp[i] * kv;
              g += tmp[i + 1] * kv;
              b += tmp[i + 2] * kv;
              a += tmp[i + 3] * kv;
            }
            const o = (y * w + x) * 4;
            out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
          }
        }
        return out;
      };
      const otsu = (hist, total) => {
        let sum = 0;
        for (let t = 0; t < 256; t++) sum += t * hist[t];
        let sumB = 0, wB = 0, varMax = 0, thr = 127;
        for (let t = 0; t < 256; t++) {
          wB += hist[t];
          if (!wB) continue;
          const wF = total - wB;
          if (!wF) break;
          sumB += t * hist[t];
          const mB = sumB / wB, mF = (sum - sumB) / wF;
          const val = wB * wF * (mB - mF) ** 2;
          if (val > varMax) { varMax = val; thr = t; }
        }
        return thr;
      };

      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = img.data;
      const W = canvas.width, H = canvas.height;

      (function unsharpMask(amount = 1.95, sigma = 3.8, threshold = 22) {
        const src = new Uint8ClampedArray(d);
        const blurred = gaussianBlur(src, W, H, sigma);
        for (let i = 0; i < d.length; i += 4) {
          const hr = src[i] - blurred[i];
          const hg = src[i + 1] - blurred[i + 1];
          const hb = src[i + 2] - blurred[i + 2];
          const mag = Math.max(Math.abs(hr), Math.abs(hg), Math.abs(hb));
          if (mag > threshold) {
            d[i] = (src[i] + amount * hr) | 0;
            d[i + 1] = (src[i + 1] + amount * hg) | 0;
            d[i + 2] = (src[i + 2] + amount * hb) | 0;
          } else {
            d[i] = src[i];
            d[i + 1] = src[i + 1];
            d[i + 2] = src[i + 2];
          }
        }
      })();

      (function channelAdjust() {
        const rG = 0.35, gG = 1.0, bG = 0.43;
        const rO = -63, gO = -4, bO = -63;
        for (let i = 0; i < d.length; i += 4) {
          d[i] = (d[i] * rG + rO) | 0;
          d[i + 1] = (d[i + 1] * gG + gO) | 0;
          d[i + 2] = (d[i + 2] * bG + bO) | 0;
        }
      })();

      (function grayscale() {
        for (let i = 0; i < d.length; i += 4) {
          const gray = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          const v = (gray < 0 ? 0 : gray > 255 ? 255 : gray) | 0;
          d[i] = d[i + 1] = d[i + 2] = v;
        }
      })();

      (function brightnessContrast() {
        const brightness = 32;
        const contrast = 3.0;
        const intercept = 128 * (1 - contrast) + brightness;
        for (let i = 0; i < d.length; i += 4) {
          const v = (d[i] * contrast + intercept) | 0;
          const vv = (v < 0 ? 0 : v > 255 ? 255 : v) | 0;
          d[i] = d[i + 1] = d[i + 2] = vv;
          d[i + 3] = 255;
        }
      })();

      (function levels() {
        const inBlack = 30, inWhite = 300, gamma = 0.5, outBlack = 0, outWhite = 255;
        const invRange = 1 / Math.max(1, (inWhite - inBlack));
        const gammaPow = 1 / Math.max(1e-6, gamma);
        for (let i = 0; i < d.length; i += 4) {
          for (let c = 0; c < 3; c++) {
            let v = d[i + c];
            const t = Math.min(1, Math.max(0, (v - inBlack) * invRange));
            v = outBlack + (outWhite - outBlack) * Math.pow(t, gammaPow);
            d[i + c] = (v < 0 ? 0 : v > 255 ? 255 : v) | 0;
          }
        }
      })();

      (function invert() {
        for (let i = 0; i < d.length; i += 4) {
          d[i] = 255 - d[i];
          d[i + 1] = 255 - d[i + 1];
          d[i + 2] = 255 - d[i + 2];
        }
      })();

      (function softThreshold() {
        const softness = 33;
        const shift = -15;
        const hist = new Uint32Array(256);
        for (let i = 0; i < d.length; i += 4) hist[d[i]]++;
        const total = W * H;
        let thr = otsu(hist, total);
        thr = Math.max(0, Math.min(255, thr + shift));
        for (let i = 0; i < d.length; i += 4) {
          const diff = d[i] - thr;
          let v;
          if (Math.abs(diff) >= softness) {
            v = diff > 0 ? 255 : 0;
          } else {
            v = (((diff + softness) / (2 * softness)) * 255) | 0;
          }
          d[i] = d[i + 1] = d[i + 2] = v;
        }
      })();

      if (roi === ROI.level || roi?.id === 'level') {
        (function verticalEnhance() {
          const src = new Uint8ClampedArray(d);
          const idx = (x, y) => (y * W + x) * 4;
          for (let y = 1; y < H - 1; y++) {
            for (let x = 0; x < W; x++) {
              const i0 = idx(x, y + 1);
              const i1 = idx(x, y);
              const i2 = idx(x, y + 1);
              const m = Math.max(src[i0], src[i1], src[i2]);
              d[i1] = d[i1 + 1] = d[i1 + 2] = m;
            }
          }
        })();
      }

      dilateOnce(img.data, canvas);
      ctx.putImageData(img, 0, 0);
    }
    return canvas.toDataURL('image/png');
  }

  /* ---------- 직업 각인 ROI 캡처 ---------- */
  function captureJobEngravingROI(invert = true) {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) return null;
    const { scaleX, scaleY, ox, oy } = getContentGeom(video);
    const rc = roiFullToContent(ROI.jobEngraving);
    const sx = Math.round(ox + rc.x * scaleX);
    const sy = Math.round(oy + rc.y * scaleY);
    const sw = Math.round(rc.w * scaleX);
    const sh = Math.round(rc.h * scaleY);
    const up = 3;
    const canvas = document.createElement('canvas');
    canvas.width = sw * up;
    canvas.height = sh * up;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(v, sx, sy, sw, sh, 0, 0, sw * up, sh * up);

    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = img.data;

    const contrast = 2.1, brightness = 0;
    const intercept = 128 * (1 - contrast) + brightness;
    for (let i = 0; i < d.length; i += 4) {
      let r = d[i] * contrast + intercept;
      let g = d[i + 1] * contrast + intercept;
      let b = d[i + 2] * contrast + intercept;
      d[i] = Math.min(255, Math.max(0, r));
      d[i + 1] = Math.min(255, Math.max(0, g));
      d[i + 2] = Math.min(255, Math.max(0, b));
    }
    ctx.putImageData(img, 0, 0);

    const gimg = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const gd = gimg.data;
    const hist = new Array(256).fill(0);
    for (let i = 0; i < gd.length; i += 4) {
      const g = 0.299 * gd[i] + 0.587 * gd[i + 1] + 0.114 * gd[i + 2];
      gd[i] = gd[i + 1] = gd[i + 2] = g;
      hist[Math.round(g)]++;
    }
    const total = canvas.width * canvas.height;
    let sum = 0; for (let t = 0; t < 256; t++) sum += t * hist[t];
    let sumB = 0, wB = 0, varMax = 0, thr = 127;
    for (let t = 0; t < 256; t++) {
      wB += hist[t]; if (!wB) continue;
      const wF = total - wB; if (!wF) break;
      sumB += t * hist[t];
      const mB = sumB / wB, mF = (sum - sumB) / wF;
      const val = wB * wF * (mB - mF) ** 2;
      if (val > varMax) { varMax = val; thr = t; }
    }
    const softness = 50;
    for (let i = 0; i < gd.length; i += 4) {
      const g = gd[i];
      const dff = g - (thr - 5);
      let v;
      if (Math.abs(dff) >= softness) v = dff > 0 ? 255 : 0;
      else v = Math.round(((dff + softness) / (2 * softness)) * 255);
      const out = invert ? 255 - v : v;
      gd[i] = gd[i + 1] = gd[i + 2] = out;
      gd[i + 3] = 255;
    }
    ctx.putImageData(gimg, 0, 0);
    return canvas.toDataURL('image/png');
  }

  /* ---------- 전투 화면 ROI 캡처(값/이름/메타) ---------- */
  function captureBattleROI(roi, mode = 'value') {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) return null;
    const { scaleX, scaleY, ox, oy } = getContentGeom(video);
    const rc = roiFullToContent(roi);
    let sx = Math.round(ox + rc.x * scaleX);
    let sy = Math.round(oy + rc.y * scaleY);
    let sw = Math.round(rc.w * scaleX);
    let sh = Math.round(rc.h * scaleY);
    const pad = (mode === 'value') ? 3 : 1;
    sx = Math.max(0, sx - pad);
    sy = Math.max(0, sy - pad);
    sw = Math.min(v.videoWidth - sx, sw + pad * 2);
    sh = Math.min(v.videoHeight - sy, sh + pad * 2);

    const up = 2;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, sw * up);
    canvas.height = Math.max(1, sh * up);
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(v, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

    const preset = (() => {
      if (mode === 'value') {
        return {
          contrast: 1,
          brightness: 0,
          invert: true,
          softness: 25,
          otsuShift: -10,
          redScale: 1.0,
          blueScale: 1.0,
          gamma: 1,
          inBlack: 0,
          inWhite: 255
        };
      }
      if (mode === 'name') {
        return {
          contrast: 1.0,
          brightness: 0,
          invert: true,
          softness: 50,
          otsuShift: 0,
          redScale: 0.9,
          blueScale: 0.9,
          gamma: 1,
          inBlack: 30,
          inWhite: 255
        };
      }
      return {
        contrast: 1.0,
        brightness: 0,
        invert: true,
        softness: 100,
        otsuShift: 0,
        redScale: 0.9,
        blueScale: 0.9,
        gamma: 1,
        inBlack: 30,
        inWhite: 255
      };
    })();

    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = img.data;
    const invRange = 1 / Math.max(1, (preset.inWhite - preset.inBlack));
    const gammaPow = 1 / Math.max(0.01, preset.gamma);
    for (let i = 0; i < d.length; i += 4) {
      let r = d[i], g = d[i + 1], b = d[i + 2];
      const applyLevel = (c) =>
        Math.pow(Math.min(1, Math.max(0, (c - preset.inBlack) * invRange)), gammaPow) * 255;
      r = applyLevel(r);
      g = applyLevel(g);
      b = applyLevel(b);
      r *= preset.redScale;
      b *= preset.blueScale;
      d[i] = r; d[i + 1] = g; d[i + 2] = b;
    }
    ctx.putImageData(img, 0, 0);

    const gray = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const gd = gray.data;
    for (let i = 0; i < gd.length; i += 4) {
      const gr = 0.299 * gd[i] + 0.587 * gd[i + 1] + 0.114 * gd[i + 2];
      gd[i] = gd[i + 1] = gd[i + 2] = gr;
    }

    const cf = preset.contrast;
    const intercept = 128 * (1 - cf) + preset.brightness;
    for (let i = 0; i < gd.length; i += 4) {
      let v = gd[i] * cf + intercept;
      v = Math.max(0, Math.min(255, v));
      gd[i] = gd[i + 1] = gd[i + 2] = v;
    }

    if (preset.invert) {
      for (let i = 0; i < gd.length; i += 4) {
        gd[i] = 255 - gd[i];
        gd[i + 1] = 255 - gd[i + 1];
        gd[i + 2] = 255 - gd[i + 2];
      }
    }

    const hist = new Array(256).fill(0);
    for (let i = 0; i < gd.length; i += 4) hist[gd[i] | 0]++;
    const total = canvas.width * canvas.height;
    let sum = 0; for (let t = 0; t < 256; t++) sum += t * hist[t];
    let sumB = 0, wB = 0, varMax = 0, thr = 127;
    for (let t = 0; t < 256; t++) {
      wB += hist[t]; if (!wB) continue;
      const wF = total - wB; if (!wF) break;
      sumB += t * hist[t];
      const mB = sumB / wB, mF = (sum - sumB) / wF;
      const between = wB * wF * (mB - mF) ** 2;
      if (between > varMax) { varMax = between; thr = t; }
    }
    thr = Math.max(0, Math.min(255, thr + (preset.otsuShift || 0)));
    const softness = preset.softness;
    for (let i = 0; i < gd.length; i += 4) {
      const diff = gd[i] - thr;
      let v;
      if (Math.abs(diff) >= softness) v = diff > 0 ? 255 : 0;
      else v = Math.round(((diff + softness) / (2 * softness)) * 255);
      gd[i] = gd[i + 1] = gd[i + 2] = v;
      gd[i + 3] = 255;
    }
    ctx.putImageData(gray, 0, 0);
    return canvas.toDataURL('image/png');
  }

  /* ---------- OCR 실행 래퍼 ---------- */
  async function runOCR(img, { lang = 'kor+eng', whitelist = '', psm = null } = {}, onStep = null) {
    if (!img) return '';
    const opts = { logger: () => { } };
    if (whitelist) opts.tessedit_char_whitelist = whitelist;
    if (psm !== null && psm !== undefined) opts.psm = psm;
    const { data: { text } } = await Tesseract.recognize(img, lang, opts);
    if (onStep) onStep();
    return (text || '');
  }
  async function runHangulOnlyOCR(imageBase64, { singleLine = true } = {}, onStep = null) {
    if (!imageBase64) return '';
    const { data: { text } } = await Tesseract.recognize(
      imageBase64,
      'kor',
      { logger: () => { }, psm: singleLine ? 7 : 6 }
    );
    const hangulOnly = (text || '').match(KEEP_HANGUL)?.join('') ?? '';
    if (onStep) onStep();
    return hangulOnly.trim();
  }
  async function ocrDigitsRobust(img, onStep) {
    if (!img) { if (onStep) onStep(); return ''; }
    const tries = [7, 13, 6, 8, 10];
    let stepped = false;

    for (const psm of tries) {
      const t = (await runOCR(img, {
        lang: 'eng',
        whitelist: '0123456789.,',
        psm,
        classify_bln_numeric_mode: 1
      })).trim();

      const raw = t.replace(/[^\d.,]/g, '');
      const normalized = (() => {
        const parts = raw.split('.');
        if (parts.length <= 2) return raw;
        const last = parts.pop();
        return parts.join('') + '.' + last;
      })();
      const digits = normalized.replace(/[^0-9.]/g, '');

      if (digits.length > 0) {
        if (onStep && !stepped) { onStep(); stepped = true; }
        return digits;
      }
    }

    if (onStep && !stepped) onStep();
    return '';
  }

  /* ---------- 화면 공유 시작 ---------- */
  async function startCapture() {
    try {
      setBattleMainData({});
      setProfileInfo(prev => ({
        job: me?.job || prev.job || '',
        jobEngraving: me?.engraving || prev.jobEngraving || '',
        level: '',
        power: '',
      }));
      setError(null);

      setLoading(true);
      setViewMode('live');
      setStep('battleMain');

      const supports = navigator.mediaDevices.getSupportedConstraints?.() || {};
      const videoConstraints = { width: 1922, height: 1113 };
      if (supports.cursor) videoConstraints.cursor = 'never';

      const captureStream = await navigator.mediaDevices.getDisplayMedia({
        video: videoConstraints,
        audio: false,
      });

      setStream(captureStream);

      if (videoRef.current) {
        videoRef.current.srcObject = captureStream;
        await new Promise(resolve => {
          const v = videoRef.current;
          const ready = () => resolve();
          if (v.readyState >= 2 && v.videoWidth) resolve();
          else v.onloadedmetadata = ready;
        });
        await videoRef.current.play();
      }
    } catch (e) {
      setError('화면 공유 실패');
      setStep('idle');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  /* ---------- 프로필 OCR 파이프라인 ---------- */

  // eslint-disable-next-line no-unused-vars
  async function processProfileStep() {
    setError(null);
    setLoading(true);

    const isMine = await verifyMyCharMarker().catch(() => false);
    if (!isMine) {
      setLoading(false);
      setError('본인 캐릭터로 진행해 주세요.');
      return;
    }

    const plan = 4;
    setStepPlan('profile', plan);
    try {
      const jobTxt = await runHangulOnlyOCR(
        captureROIBase64(ROI.job, false),
        {},
        () => bumpStepProgress('profile')
      );
      const rawJob = (jobTxt || '').replace(/\s+/g, '');
      const { best: bestJob, score: jobScore } = findBestMatchWithScore(rawJob, KNOWN_JOBS);
      if (jobScore < 0.4) {
        setError('인식 실패 (직업)');
        setLoading(false);
        return;
      }
      const job = bestJob;

      const lvlDigits = await ocrDigitsRobust(
        captureROIBase64(ROI.level, true),
        () => bumpStepProgress('profile')
      );
      const pwrDigits = await ocrDigitsRobust(
        captureROIBase64(ROI.power, true),
        () => bumpStepProgress('profile')
      );

      const jeTxtRaw = await runHangulOnlyOCR(
        captureJobEngravingROI(),
        {},
        () => bumpStepProgress('profile')
      );
      const candidates = (JOB_CONFIG[job]?.engravings || []).map(e => e.name);
      const allEngs = candidates.length
        ? candidates
        : Object.values(JOB_CONFIG)
          .filter(v => v && Array.isArray(v.engravings))
          .flatMap(v => v.engravings.map(e => e.name));
      const { best: bestEng, score: jobEngScore } = bestEngravingMatch(jeTxtRaw, allEngs);
      if (jobEngScore < 0.4) {
        setError('인식 실패 (직업 각인)');
        setLoading(false);
        return;
      }
      const jobEngraving = bestEng;

      const level = normalizeFixed4_2(lvlDigits);
      const power = normalizeFixed4_2(pwrDigits);

      setProfileInfo({ job, jobEngraving, level, power });
      setStep('battleMain');
      setLoading(false);
    } catch (e) {
      setError(e?.message || String(e));
      setLoading(false);
    }
  }

  /* ---------- 메인 화면 OCR (1차) ---------- */
  async function processBattleMain() {
    setError(null);
    const activeJob = displayJob || '알수없음';
    const activeEngraving = battleMainData.jobEngraving || displayEngraving || '';
    const { mainCount, addCount, traitNames } = getJobConfig(activeJob, activeEngraving);

    let availableMain = 0;
    for (let i = 1; i <= mainCount; i++) {
      if (ROI[`mainTrait${i}`] && ROI[`mainTrait${i}Value`]) availableMain++; else break;
    }
    const FIRST_PAGE_MAX = 14;
    const firstPageEnd = Math.min(FIRST_PAGE_MAX, addCount);
    let availableAddFirst = 0;
    for (let i = 1; i <= firstPageEnd; i++) {
      if (ROI[`addTrait${i}`] && ROI[`addTrait${i}Value`]) availableAddFirst++; else break;
    }
    const plan = 3 + (availableMain * 2) + (availableAddFirst * 2);
    setStepPlan('battleMain', plan);

    try {
      const rawRaidName = (await runOCR(
        captureBattleROI(ROI.raidName, 'meta'),
        { lang: 'kor+eng', psm: 7 },
        () => bumpStepProgress('battleMain')
      )).trim();

      const normRaw = normalizeRaidNameForMatch(rawRaidName);
      const { stage: rawStage, gate: rawGate } = extractStageAndGate(normRaw);
      const rawCompact = compact(normRaw);

      let bestName = rawRaidName;
      let bestScore = -Infinity;
      let bestBaseSim = 0;
      for (const cand of RAID_NAMES) {
        const normCand = normalizeRaidNameForMatch(cand);
        const candCompact = compact(normCand);
        const { stage: candStage, gate: candGate } = extractStageAndGate(normCand);
        const baseSim = similarity(rawCompact, candCompact);
        let score = baseSim;
        if (rawGate && candGate && rawGate === candGate) score += 0.20;
        if (rawStage && candStage && rawStage === candStage) score += 0.10;
        if (score > bestScore) {
          bestScore = score;
          bestBaseSim = baseSim;
          bestName = cand;
        }
      }
      const raidName = bestName;
      if (bestBaseSim < 0.45) {
        setError('인식 실패 (레이드명)');
        setLoading(false);
        return;
      }

      // ✅ OCR 직후 공격대 설정과 불일치 여부를 즉시 점검 → StepProgressBar에 에러 표시
      checkRaidGateMismatch(raidName);

      const rawBattleTime = (await runOCR(captureBattleROI(ROI.battleTime, 'meta'), { lang: 'eng', whitelist: '0123456789:' }, () => bumpStepProgress('battleMain'))).trim();
      const battleTimeTxt = (() => {
        const parts = (rawBattleTime || '').split(':').map(p => parseInt(p, 10) || 0);
        let totalSec = 0;
        if (parts.length === 3) totalSec = parts[0] * 3600 + parts[1] * 60 + parts[2];
        else if (parts.length === 2) totalSec = parts[0] * 60 + parts[1];
        else totalSec = parts[0] || 0;
        const m = String(Math.floor(totalSec / 60)).padStart(2, '0');
        const s = String(totalSec % 60).padStart(2, '0');
        return `${m}:${s}`;
      })();

      const rawTimestamp = (await runOCR(captureBattleROI(ROI.timestamp, 'meta'), { lang: 'eng', whitelist: '0123456789.:-/ ' }, () => bumpStepProgress('battleMain'))).trim();
      const timestamp = normalizeTimestamp(rawTimestamp);

      const mainNameRaws = [];
      const mainValueRois = [];
      for (let i = 1; i <= availableMain; i++) {
        const nameROI = ROI[`mainTrait${i}`];
        const valueROI = ROI[`mainTrait${i}Value`];
        const nameRaw = (await runOCR(captureBattleROI(nameROI, 'name'), { lang: 'kor' }, () => bumpStepProgress('battleMain'))).trim();
        mainNameRaws.push(nameRaw);
        mainValueRois.push(valueROI);
      }

      const mainMatches = uniqueMatchWithScores(mainNameRaws, traitNames);
      const mainTraits = [];
      for (let idx = 0; idx < mainNameRaws.length; idx++) {
        const match = mainMatches[idx];
        const nameRaw = mainNameRaws[idx];
        if (!match || match.score < 0.4) {
          setError(`인식 실패 (메인 특성${idx + 1}: ${nameRaw})`);
          setLoading(false);
          return;
        }
        const traitName = match.best;
        const valueRaw = (await runOCR(
          captureBattleROI(mainValueRois[idx], 'value'),
          { lang: 'kor+eng', whitelist: '0123456789.,%만억조회', psm: 7 },
          () => bumpStepProgress('battleMain')
        )).trim();
        const formatted = formatValueWithUnit(valueRaw, traitName);
        mainTraits.push({ trait: traitName, value: formatted });
      }

      const addNameRaws = [];
      const addValueRois = [];
      const addRowIndex = [];
      let consecutiveSkips = 0;
      for (let i = 1; i <= firstPageEnd; i++) {
        const raw = (await runOCR(captureBattleROI(ROI[`addTrait${i}`], 'name'), { lang: 'kor' }, () => bumpStepProgress('battleMain'))).trim();
        if (!raw) {
          consecutiveSkips++;
          if (consecutiveSkips >= 2) break;
          continue;
        }
        consecutiveSkips = 0;
        addNameRaws.push(raw);
        addValueRois.push(ROI[`addTrait${i}Value`]);
        addRowIndex.push(i);
      }
      const addMatches = uniqueMatchWithScores(addNameRaws, traitNames);
      const additionalTraits_first = [];
      for (let k = 0; k < addNameRaws.length; k++) {
        const match = addMatches[k];
        const nameRaw = addNameRaws[k];
        if (!match || match.score < 0.4) {
          setError(`인식 실패 (추가 특성${addRowIndex[k]}: ${nameRaw})`);
          setLoading(false);
          return;
        }
        const traitName = match.best;
        const valueRaw = (await runOCR(
          captureBattleROI(addValueRois[k], 'value'),
          { lang: 'kor+eng', whitelist: '0123456789.,%만억조회', psm: 7 },
          () => bumpStepProgress('battleMain')
        )).trim();

        // const jeImg = captureBattleROI(addValueRois[0], 'value');
        // const imgEl = document.createElement('img');
        // imgEl.src = jeImg;
        // imgEl.style.position = 'fixed';
        // imgEl.style.top = '10px';
        // imgEl.style.right = '10px';
        // imgEl.style.border = '2px solid red';
        // imgEl.style.zIndex = 9999;
        // imgEl.style.maxWidth = '200px';
        // document.body.appendChild(imgEl);

        const formatted = formatValueWithUnit(valueRaw, traitName);
        additionalTraits_first.push({ trait: traitName, value: formatted });
      }

      enforceDamageRateUnits(mainTraits, additionalTraits_first);

      if (addCount > FIRST_PAGE_MAX) {
        partialRef.current = { raidName, battleTime: battleTimeTxt, timestamp, mainTraits, additionalTraits: additionalTraits_first };
        setStep('battleMain2');
        setLoading(false);
        return;
      }

      setBattleMainData({ raidName, battleTime: battleTimeTxt, timestamp, mainTraits, additionalTraits: additionalTraits_first });
      setStep('done');
      setLoading(false);
    } catch (e) {
      setError(e?.message || String(e));
      setLoading(false);
    }
  }

  /* ---------- 메인 화면 OCR (2차: 15~) ---------- */
  async function processBattleMain2() {
    setError(null);
    const job = displayJob || '알수없음';
    const { addCount, traitNames } = getJobConfig(job, displayEngraving || '');
    const startIdx = 15;
    const endIdx = Math.min(addCount, 18);
    const map = (addCount <= 16) ? ROI_SECOND_PASS_16 : ROI_SECOND_PASS_18;

    const plan = (endIdx - startIdx + 1) * 2;
    setStepPlan('battleMain2', plan);

    try {
      const base = partialRef.current || {};
      const existedNames = new Set([
        ...((base.mainTraits || []).map(t => t.trait)),
        ...((base.additionalTraits || []).map(t => t.trait)),
      ]);

      const addNameRaws2 = [];
      const addValueRois2 = [];
      const addRowIndex2 = [];
      for (let i = startIdx; i <= endIdx; i++) {
        const nameROI = map[`addTrait${i}`];
        const valueROI = map[`addTrait${i}Value`];
        const raw = (await runOCR(captureBattleROI(nameROI, 'name'), { lang: 'kor' }, () => bumpStepProgress('battleMain2'))).trim();
        addNameRaws2.push(raw);
        addValueRois2.push(valueROI);
        addRowIndex2.push(i);
      }

      const addMatches2 = uniqueMatchWithScores(addNameRaws2, traitNames);
      const additionalTraits_second = [];
      for (let k = 0; k < addNameRaws2.length; k++) {
        const match = addMatches2[k];
        const nameRaw = addNameRaws2[k];
        if (!match || match.score < 0.4) {
          setError(`인식 실패 (추가 특성${addRowIndex2[k]}: ${nameRaw})`);
          setLoading(false);
          return;
        }
        const traitName = match.best;
        if (existedNames.has(traitName)) {
          setError('전투 분석기 스크롤 후 진행해 주세요');
          setLoading(false);
          return;
        }
        const valueRaw = (await runOCR(
          captureBattleROI(addValueRois2[k], 'value'),
          { lang: 'kor+eng', whitelist: '0123456789.,%만억조회', psm: 7 },
          () => bumpStepProgress('battleMain2')
        )).trim();
        const formatted = formatValueWithUnit(valueRaw, traitName);
        additionalTraits_second.push({ trait: traitName, value: formatted });
      }

      const mergedAdd = [].concat(base.additionalTraits || [], additionalTraits_second);
      enforceDamageRateUnits(base.mainTraits || [], mergedAdd);

      setBattleMainData({
        raidName: base.raidName || '',
        battleTime: base.battleTime || '',
        timestamp: base.timestamp || '',
        mainTraits: base.mainTraits || [],
        additionalTraits: mergedAdd,
      });
      partialRef.current = null;
      setStep('done');
      setLoading(false);
    } catch (e) {
      setError(e?.message || String(e));
      setLoading(false);
    }
  }

  /* ---------- 내 캐릭터 마커 검증 ---------- */

  // eslint-disable-next-line no-unused-vars
  async function verifyMyCharMarker() {
    const candidates = [
      { roi: MYCHAR_ROI, target: '1' },
      { roi: MYCHAR_ROI2, target: '2' },
    ];

    for (const { roi, target } of candidates) {
      const img = captureROIBase64(roi, false);
      if (!img) continue;

      const d1 = await ocrDigitsRobust(img);
      if (String(d1 || '').trim() === target) return true;

      const raw = (await runOCR(img, {
        lang: 'eng',
        whitelist: '012Il|! ',
        psm: 7,
      })).trim();

      const normalized = raw.replace(/[|!]/g, '1');
      const fixed = fixDigitLookalikes(normalized);
      const digits = fixed.replace(/\D/g, '');
      if (digits === target) return true;
    }
    return false;
  }

  /* ---------- 저장/불러오기/클라우드 동기화 ---------- */
  async function saveRaid() {
    if (!uid || !me || !party) return;

    const {
      raidName: ocrRaidName,
      battleTime,
      timestamp,
      mainTraits,
      additionalTraits,
    } = battleMainData || {};
    const resolvedRaidName = ocrRaidName || raidName || "";

    const raidBaseRaw =
      party?.raidBaseName ??
      party?.raidName ??
      "";

    const gateRawFromParty =
      party?.gateName ??
      party?.selectedGate ??
      party?.gate ??
      "";

    const effectiveGate = gateName || gateRawFromParty;

    const rn = normForCompare(resolvedRaidName);
    const rb = normForCompare(raidBaseRaw);
    const gg = normForCompare(effectiveGate);

    if (!(rn && rn.includes(rb) && rn.includes(gg))) {
      alert(`현재 설정된 레이드/관문("${raidBaseRaw} ${effectiveGate}")과 OCR("${resolvedRaidName}")이 일치해야 저장됩니다.`);
      return;
    }

    if (!resolvedRaidName || !battleTime) {
      alert("전투 분석기 인식 결과가 비어있습니다.");
      return;
    }

    // 🔎 내 기존 기록(현재 관문 기준) 찾기 → 있으면 덮어쓰기
    const egNum = getGateNumber(effectiveGate);
    const norm = (s) => String(s || '').replace(/\s+/g, '');
    const myExistingForGate = (analyses || [])
      .filter(a =>
        a?.payload?.type !== '__gate__' &&
        a?.payload?.type !== '__avg__' &&   // ← 평균 문서는 제외!
        a?.uid === uid &&
        (
          (egNum && getGateNumber(getRecordGate(a)) && egNum === getGateNumber(getRecordGate(a))) ||
          (!egNum && norm(getRecordGate(a)) === norm(effectiveGate))
        )
      )
      .sort((a, b) => recordSeconds(b) - recordSeconds(a))[0]; // 최신 1개

    try {

      const powerToSave = (() => {
        if (Number.isFinite(loaInfo?.combatPower)) return Math.round(loaInfo.combatPower * 100) / 100;
        if (Number.isFinite(me?.power)) return Math.round(me.power * 100) / 100;
        const n = parseFloat(profileInfo.power);
        return Number.isFinite(n) ? Math.round(n * 100) / 100 : undefined;
      })();

      const levelToSave = (() => {
        if (Number.isFinite(loaInfo?.itemAvgLevel)) return Math.round(loaInfo.itemAvgLevel * 100) / 100;
        if (Number.isFinite(me?.level)) return Math.round(me.level * 100) / 100;
        const n = parseFloat(profileInfo.level);
        return Number.isFinite(n) ? Math.round(n * 100) / 100 : undefined;
      })();

      await savePartyAnalysis({
        code,
        uid,
        nickname: me.nickname,
        job: displayJob,
        engraving: displayEngraving,
        power: powerToSave,
        raidName: resolvedRaidName,
        level: levelToSave,
        // ✨ upsert 포인트: 기존 문서가 있으면 그 문서 id로 저장(업데이트)
        ...(myExistingForGate?.id ? { docId: myExistingForGate.id } : {}),
        payload: {
          type: "__rec__",
          raidName: resolvedRaidName,
          battleTime,
          timestamp,
          mainTraits,
          additionalTraits,
          memo: memo?.trim() || "",
          targetRaidBase: raidBaseRaw,
          targetGate: effectiveGate,
          gateName: effectiveGate,
        },
      });

      setMemo("");
      await loadAll();
      alert(myExistingForGate ? "기존 공유 기록을 업데이트했습니다." : "공격대 기록에 저장했습니다.");
    } catch (e) {
      console.error(e);
      alert("저장 실패: " + (e?.message || e));
    }
  }


  const handleSelect = (vOrEvent) => {
    if (Array.isArray(vOrEvent)) {
      setSelectedIds(vOrEvent);
    } else {
      const values = Array.from(vOrEvent?.target?.selectedOptions ?? []).map(o => o.value);
      setSelectedIds(values);
    }
  };
  function handleReProfile() {
    setProgressByStep(prev => ({
      ...prev,
      profile: { done: 0, total: 0 },
      battleMain: { done: 0, total: 0 },
      battleMain2: { done: 0, total: 0 },
    }));
    setProfileInfo({ job: '', jobEngraving: '', level: '', power: '' });
    setBattleMainData({});
    setStep('profile');
  }
  function rerunBattle() {
    setProgressByStep(prev => ({
      ...prev,
      battleMain: { done: 0, total: 0 },
      battleMain2: { done: 0, total: 0 },
    }));
    setStep('battleMain');
    setBattleMainData({});
  }

  // 공격대계산 트리거 함수

  const handleCalcRaidAvg = useCallback(async () => {
    if (!isLeader) return;
    // visibleAnalyses: 이미 같은 관문 + 공격대원별 최신 1개만 필터된 목록
    const avg = computeRaidAverageFrom(visibleAnalyses, JOB_CONFIG, parseRawValue);
    if (!avg) {
      alert('딜러 데이터를 찾을 수 없습니다.');
      return;
    }
    // CHANGED: 저장 값 표준화(소수 2자리 고정) & 중복 검사
    const round2 = (x) => Math.round((x || 0) * 100) / 100;
    const battleTimeAvg = secToMMSS(avg.avgSec);
    const dealerCount = avg.dealerCount;
    const avgTotalRaw = round2(avg.avgTotal);
    const avgDpsRaw = round2(avg.avgDps);

    // 현재 관문 스냅샷 중 동일 값 존재하면 중복 저장 차단
    const egKey = normForCompare(effectiveGate || "");
    const isDup = (raidAvgAll || []).some(doc => {
      const p = doc?.payload || {};
      return (
        normForCompare(p.gateName || "") === egKey &&
        round2(Number(p.avgTotalRaw || 0)) === avgTotalRaw &&
        round2(Number(p.avgDpsRaw || 0)) === avgDpsRaw &&
        String(p.battleTimeAvg || '') === battleTimeAvg &&
        Number(p.dealerCount || 0) === dealerCount
      );
    });
    if (isDup) {
      alert('동일한 공격대 평균이 이미 있습니다.');
      return;
    }
    // 저장 → 모든 클라이언트 onSnapshot으로 즉시 반영
    try {
      await savePartyAverageSnapshot({
        code,
        gateName: effectiveGate,
        battleTimeAvg,
        dealerCount,
        avgTotalRaw,
        avgDpsRaw,
        createdBy: {
          uid,
          nickname: me?.nickname || "",
          job: me?.job || "",
          engraving: me?.engraving || "",
        },
      });
    } catch (e) {
      console.error(e);
      alert('평균 저장 실패: ' + (e?.message || e));
    }
  }, [isLeader, visibleAnalyses, code, uid, me, effectiveGate, raidAvgAll]);


  const handleShowRaidAvg = useCallback(() => {
    setRaidAvgModalOpen(true);
  }, []);



  /* ---------- 공유된 리스트 관련 ---------- */

  // 선택된 공격대 기록 로드(visibleAnalyses 기반)
  function loadPartyRecord() {
    if (selectedIds.length !== 1) return;
    const rec = visibleAnalyses.find(r => r.id === selectedIds[0]);
    if (!rec) return;

    const p = rec.payload || {};
    setBattleMainData({
      raidName: p.raidName || '',
      timestamp: p.timestamp || '',
      battleTime: p.battleTime || '',
      mainTraits: p.mainTraits || [],
      additionalTraits: p.additionalTraits || [],
      job: rec.job || '',
      jobEngraving: rec.engraving || '',
      memo: p.memo || '',
    });
    setProfileInfo({
      job: rec.job || '',
      jobEngraving: rec.engraving || '',
      level: rec.level || '',
      power: rec.power || '',
    });
    setStep('done');

    // 화면공유 중지 및 오버레이 정리(기존 loadRaid와 동일)
    try {
      if (stream) stream.getTracks().forEach(t => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
      const c = overlayRef.current;
      if (c) c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
    } catch { }
    setStream(null);
    setViewMode('live');
  }

  // 기록의 관문 문자열 통합 추출
  function getRecordGate(rec) {
    return rec?.payload?.gateName ?? rec?.payload?.targetGate ?? rec?.gateName ?? '';
  }
  // 기록의 unix seconds 타임스탬프 추출(정렬용)
  function recordSeconds(rec) {
    const s = rec?.createdAt?.seconds;
    if (Number.isFinite(s)) return s;
    const ms = rec?.createdAt?.toMillis?.();
    if (Number.isFinite(ms)) return Math.floor(ms / 1000);
    // 옛 필드 폴백
    const t = rec?.timestamp;
    if (typeof t === 'number') return t;
    return 0;
  }

  // 모두 비우기 / 해산
  const clearAllPartyAnalyses = async () => {
    if (!isLeader) return;
    if (!window.confirm('정말 공격대의 저장된 기록을 모두 비울까요?')) return;
    await clearPartyAnalysesClient(code);
    await loadAll();
    alert('기록을 모두 비웠습니다.');
  };
  const disbandParty = async () => {
    if (!isLeader) return;
    if (!window.confirm('정말 공격대를 해산할까요? 이 작업은 되돌릴 수 없습니다.')) return;
    try {
      await disbandPartyClient(code);
      alert('공격대를 해산했습니다.');
      navigate('/lobby', { replace: true }); // 뒤로가기 시 파티 페이지로 안 돌아오게
    } catch (e) {
      console.error(e);
      alert('해산 실패: ' + (e?.message || e));
    }
  };

  /* ---------- 비교/평균 계산 ---------- */
  useEffect(() => {
    let raf;
    const tick = () => { drawOverlay(); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [drawOverlay]);

  useEffect(() => {
    const onResize = () => drawOverlay();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [drawOverlay]);

  // eslint-disable-next-line no-unused-vars
  const mainView = battleMainData.mainTraits || [];
  const addView = battleMainData.additionalTraits || [];

  const viewROI = ROI_PRESETS[step] || ROI_PRESETS.idle;

  /* ---- 렌더 ---- */
  // ⬇️ 분기는 "훅 호출 후"에만 — 훅을 먼저 모두 호출합니다.
  // 생성일/삭제 예정일 (항상 호출되도록 early return 보다 위로 이동)
  const partyCreatedAt = useMemo(() => toDateFromFirestore(party?.createdAt), [party]);
  const partyExpireAt  = useMemo(() => {
    if (party?.expireAt) return toDateFromFirestore(party.expireAt);
    if (!partyCreatedAt) return null;
    return new Date(partyCreatedAt.getTime() + 7 * 24 * 60 * 60 * 1000);
  }, [party, partyCreatedAt]);

  if (!authUser) {
    return (
      <div className="mx-auto max-w-3xl p-6 text-zinc-600 dark:text-zinc-300">
        Google 로그인 후 이용해주세요.
      </div>
    );
  }
  if (bootHold) {
    return (
      <Loader
        mode="fullscreen"
        loading={true}
        message="불러오는 중…"
        minDuration={0}
        backdrop
      />
    );
  }
  if (!party || !me) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="text-center text-sm text-red-600 dark:bg-zinc-900 dark:border-zinc-300/10 dark:text-red-300">
          {"접근 권한이 없거나 공격대가 존재하지 않습니다."}
        </div>
      </div>
    );
  }
  // eslint-disable-next-line no-unused-vars
  const viewROIBox = viewROI;

  // ✅ 화면에 “몇 관문” 표시
  // eslint-disable-next-line no-unused-vars
  const gateNo =
    getGateNumber(effectiveGate) ??
    getGateNumber(gateName) ??
    getGateNumber(raidName);


  const chevronSvg = encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#9CA3AF"><path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.17l3.71-2.94a.75.75 0 1 1 .92 1.18l-4.17 3.31a.75.75 0 0 1-.92 0L5.21 8.41a.75.75 0 0 1 .02-1.2z"/></svg>'
  );

  const selectCls =
    // 레이아웃/모양
    "appearance-none inline-block rounded-lg border border-zinc-950/10 cursor-pointer " +
    "px-2 py-1.5 pr-8 bg-white " +
    // 폰트/컬러
    "text-sm font-semibold text-zinc-950 transition-all " +
    // 상호작용
    "focus:outline-none focus:ring-1 focus:ring-indigo-500 " +
    "disabled:bg-zinc-100 disabled:text-zinc-300 disabled:cursor-not-allowed " +
    // 화살표 배치
    "bg-no-repeat bg-[length:16px_16px] bg-[right_8px_center] " +
    // 🔽 다크모드
    "dark:bg-zinc-900 dark:text-zinc-100 dark:border-white/10 " +
    "dark:focus:ring-indigo-400 dark:disabled:bg-white/5 dark:disabled:text-zinc-500";


  /* ---------- 렌더 ---------- */

  // 복사버튼과 툴팁

function CopyCodeButton({ code, variant = 'primary', className = '' }) {
  const [mounted, setMounted] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  const timers = React.useRef([]);

  const clearTimers = () => {
    timers.current.forEach(t => clearTimeout(t));
    timers.current = [];
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);

      // 재클릭 대비: 기존 타이머/상태 정리
      clearTimers();
      setMounted(true);
      // 다음 프레임에 visible=true → 트랜지션 시작
      requestAnimationFrame(() => setVisible(true));

      // 1) 보여주는 시간
      timers.current.push(setTimeout(() => setVisible(false), 2000)); // 1.2s 뒤 fade-out
      // 2) 트랜지션 종료 후 언마운트 (duration-200 기준 여유 200ms)
      timers.current.push(setTimeout(() => setMounted(false), 2200));
    } catch {
      // noop
    }
  };

  React.useEffect(() => {
    return clearTimers; // 언마운트 시 타이머 정리
  }, []);

  const variantCls = variant === 'secondary' ? 'btn-secondary' : 'btn-primary';

  // 툴팁 배경/꼬리 색상 (variant 연동)
  const tooltipBg =
    variant === 'secondary'
      ? 'bg-zinc-700 text-white dark:bg-zinc-600'
      : 'bg-indigo-600 text-white dark:bg-indigo-500';
  const tooltipArrow =
    variant === 'secondary'
      ? 'border-t-zinc-700 dark:border-t-zinc-600'
      : 'border-t-indigo-600 dark:border-t-indigo-500';

  return (
    <div className="relative inline-block">
      <button
        type="button"
        className={`btn btn-sm ${variantCls} !gap-1 ${className}`}
        onClick={onCopy}
        aria-label="코드 복사"
        aria-describedby={mounted && visible ? 'copied-tip' : undefined}
        title="코드 복사"
      >
        <Copy size={16} /> 복사
      </button>

      {mounted && (
        <div
          id="copied-tip"
          role="status"
          className={`absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full
                      rounded-md px-2 py-1 text-xs shadow-md whitespace-nowrap pointer-events-none
                      ${tooltipBg}
                      transition-all duration-200 ease-out
                      ${visible ? 'opacity-100 -translate-y-full' : 'opacity-0 -translate-y-5'}`}
        >
          복사 완료
          <div
            className={`absolute left-1/2 -bottom-1 -translate-x-1/2
                        w-0 h-0 border-x-4 border-x-transparent border-t-4 ${tooltipArrow}`}
          />
        </div>
      )}
    </div>
  );
}


  // 스냅샷 → 차트 데이터 (오래된→최신 순서, No 1..N)
  const chartData = (raidAvgSnapshots || [])
    .slice()
    .reverse()
    .map((doc, idx) => {
      const p = doc?.payload || {};
      return {
        no: idx + 1,
        total: Number(p.avgTotalRaw || 0),
        dps: Number(p.avgDpsRaw || 0),
        time: p.battleTimeAvg || "-",
        dealers: Number(p.dealerCount || 0),
      };
    });

  // 축 도메인(±10% 여유, 정수 반올림)
  const getDomainInt = (vals) => {
    const v = vals.filter((n) => Number.isFinite(n));
    if (!v.length) return ["auto", "auto"];
    let min = Math.min(...v), max = Math.max(...v);
    if (min === max) {
      const pad = Math.max(1, Math.round(max * 0.1));
      return [Math.max(0, Math.floor(min - pad)), Math.ceil(max + pad)];
    }
    const pad = Math.max(1, Math.round((max - min) * 0.1));
    return [Math.max(0, Math.floor(min - pad)), Math.ceil(max + pad)];
  };
  const [leftMin, leftMax] = getDomainInt(chartData.map(d => d.total));
  const [rightMin, rightMax] = getDomainInt(chartData.map(d => d.dps));


  // 값 표기(툴팁/선 라벨용): 소수 2자리 유지
  const fmtValueFloat = (raw) => {
    if (!Number.isFinite(raw)) return "";
    if (raw >= 1e12) return `${(raw / 1e12).toFixed(2)}조`;
    if (raw >= 1e8) return `${(raw / 1e8).toFixed(2)}억`;
    if (raw >= 1e4) return `${(raw / 1e4).toFixed(2)}만`;
    return raw.toFixed(2);
  };

  // 툴팁(글자크기 text-sm)
  const TooltipContent = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload || {};
    return (
      <div className="rounded-md border border-zinc-200 bg-white p-3 shadow-md text-sm
                    dark:bg-zinc-900 dark:border-zinc-700">
        <div className="font-semibold text-zinc-800 dark:text-zinc-100">No {label}</div>
        <div className="mt-1 space-y-1">
          <div className="flex items-center justify-between gap-4">
            <span className="text-zinc-500 dark:text-zinc-400">피해량</span>
            <span className="font-medium text-indigo-600 dark:text-indigo-300">
              {fmtValueFloat(row.total)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-zinc-500 dark:text-zinc-400">초당 피해량</span>
            <span className="font-medium text-rose-600 dark:text-rose-300">
              {fmtValueFloat(row.dps)}
            </span>
          </div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-2">
            전투시간 {row.time} · 딜러 {row.dealers}명
          </div>
        </div>
      </div>
    );
  };




  return (
    <div className="mx-auto max-w-6xl sm:px-4 lg:px-0">

      {/* ===== 모바일 헤더 (sm 미만) ===== */}
      <header className="lg:hidden pt-2 pb-3 border-b border-zinc-200 dark:border-zinc-800">
        {/* 1행: 레이드명 · 코드/복사 */}
        <div className="flex items-center justify-between gap-2">
          <h1 className="min-w-0 text-base/6 font-semibold text-zinc-950 dark:text-white truncate">
            {party.raidName}
          </h1>

          <div className="shrink-0 flex items-center gap-1.5">
            <span className="font-mono btn btn-sm">
              {code}
            </span>
            <CopyCodeButton code={code} variant="primary" />
          </div>
        </div>

        {/* 2행: 관문 설정 */}
        <div className="mt-2 flex items-center gap-2">
          <label htmlFor="gateSelect" className="sr-only">관문</label>
          <select
            id="gateSelect"
            className={`${selectCls} h-9 min-w-[5.75rem]`}  // 5.75rem ≈ 92px
            style={{ backgroundImage: `url("data:image/svg+xml,${chevronSvg}")` }}
            value={gateName}
            onChange={(e) => setGateName(e.target.value)}
            disabled={!isLeader}
            title={isLeader ? "관문 선택" : "공대장만 변경 가능"}
            aria-label="관문 선택"
          >
            {gateOptions.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>

          {isLeader ? (
            <button
              type="button"
              onClick={saveGateSelection}
              className="btn btn-sm btn-primary"
            >
              관문 저장
            </button>
          ) : (
            <span className="text-[11px] text-zinc-500 dark:text-zinc-400">
              공대장만 변경
            </span>
          )}
        </div>

        {/* 3행: 내 정보/역할 */}
        <div className="mt-3 text-xs text-zinc-600 dark:text-zinc-300 truncate">
          <span className="font-medium text-zinc-900 dark:text-white">{me.nickname}</span>
          <span className="mx-1 opacity-60">/</span>
          <span className="text-zinc-700 dark:text-zinc-200">{displayJob || "-"}</span>
          {displayEngraving && <span className="text-zinc-500 dark:text-zinc-400"> ({displayEngraving})</span>}
          {displayLevel && (
            <>
              <span className="mx-2 opacity-40">·</span>
              <span className="text-zinc-700 dark:text-zinc-200">아이템 레벨 {displayLevel}</span>
            </>
          )}
          {displayPower && (
            <>
              <span className="mx-2 opacity-40">·</span>
              <span className="text-zinc-700 dark:text-zinc-200">전투력 {displayPower}</span>
            </>
          )}
          <span className="mx-2 opacity-40">·</span>
          <span className="font-medium text-indigo-600 dark:text-indigo-300">
            {isLeader ? "공대장" : "공격대원"}
          </span>
        </div>



          {isLeader && (
            <button
              onClick={disbandParty}
              className="mt-3 btn btn-sm btn-danger w-full"
              title="공격대를 해산합니다"
            >
              공격대 해산
            </button>
          )}

      </header>


      <h1 className="hidden lg:flex text-lg/8 font-semibold text-zinc-950 sm:text-xl/8 dark:text-white flex flex-wrap items-center gap-x-3 -mb-5">
        {/* 좌측: 레이드명 + 관문 설정 (코드 자리를 대체) */}
        <div className="flex items-center gap-3 min-w-0">
          <span className="truncate">{party.raidName}</span>

          {/* 관문 설정 */}
          <div className="flex items-center gap-2">
            <select
              className={`${selectCls} w-[6em]`}
              style={{ backgroundImage: `url("data:image/svg+xml,${chevronSvg}")` }}
              value={gateName}
              onChange={(e) => setGateName(e.target.value)}
              disabled={!isLeader}
              title={isLeader ? "관문 선택" : "공대장만 변경 가능"}
              aria-label="관문 선택"
            >
              {gateOptions.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>

            {isLeader ? (
              <button
                type="button"
                onClick={saveGateSelection}
                className="btn btn-sm btn-primary"
              >
                관문 설정 저장
              </button>
            ) : (
              <span className="text-xs text-zinc-500 dark:text-zinc-400">
                공대장이 설정한 관문만 기록됩니다.
              </span>
            )}
          </div>
        </div>

        {/* 우측: 코드/복사 + 공격대 해산(맨 끝) */}
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="font-mono btn btn-sm">
              {code}
            </span>
            <CopyCodeButton code={code} variant="primary" />
          </div>

          {isLeader && (
            <button
              onClick={disbandParty}
              className="btn btn-sm btn-danger"
              title="공격대를 해산합니다"
            >
              공격대 해산
            </button>
          )}
        </div>

        {/* (원래 관문설정 자리): 내 정보 + 역할 (텍스트 스타일) */}
        <div className="w-full mt-1 text-sm text-zinc-600 dark:text-zinc-300">
          <span className="font-medium text-zinc-900 dark:text-white">{me.nickname}</span>
          <span className="mx-1 opacity-60">/</span>
          <span className="text-zinc-700 dark:text-zinc-200">{displayJob || "-"}</span>
          {displayEngraving && <span className="text-zinc-500 dark:text-zinc-400"> ({displayEngraving})</span>}
          {displayLevel && (
            <>
              <span className="mx-2 opacity-40">·</span>
              <span className="text-zinc-700 dark:text-zinc-200">아이템 레벨 {displayLevel}</span>
            </>
          )}
          {displayPower && (
            <>
              <span className="mx-2 opacity-40">·</span>
              <span className="text-zinc-700 dark:text-zinc-200">전투력 {displayPower}</span>
            </>
          )}
          <span className="mx-2 opacity-40">·</span>
          <span className="font-medium text-indigo-600 dark:text-indigo-300">
            {isLeader ? "공대장" : "공격대원"}
          </span>
        </div>
      </h1>




      <div className="hidden lg:block mt-3">
        <ControlsBar
          stream={stream}
          step={step}
          loading={loading}
          memo={memo}
          setMemo={setMemo}
          onStartCapture={startCapture}
          // 프로필 단계 제거
          onRunProfile={undefined}
          onRunMain={processBattleMain}
          onRunMain2={processBattleMain2}
          onReProfile={handleReProfile}
          onSaveRaid={saveRaid}
          onRerunBattle={rerunBattle}
          onResetToStart={resetToStart}
          saveLabel="공유하기"
          disableStart={!canStartCapture}
          startTooltip={startTooltip}
        />
        <StepProgressBar
          step={step}
          getStepPercent={getStepPercent}
          loading={loading}
          error={stepBarError || error}
          showPage2Progress={showPage2Progress}
        />
      </div>

      {/* 안내 배너 */}
      {isLeader && (
      
      <div className="flex flex-row flex-wrap justify-between items-center mt-3 mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900 dark:bg-amber-400/10 dark:border-amber-400/30 dark:text-amber-200">
        <p>
          사용 완료 후에는 데이터 정리를 위해 반드시 <b>“공격대 해산”</b> 버튼을 눌러주세요.
          (미해산 시에도 <b>생성일로부터 7일 후</b> 자동 삭제됩니다)
          {partyExpireAt && (
            <span className="ml-2">
              · 삭제 예정: <b className="text-rose-600 dark:text-rose-300">{fmtKST(partyExpireAt)}</b>
            </span>
          )}
        </p>
        <ExtendExpireButton code={code} />
      </div>
      )}

      <div className={`flex flex-col lg:flex-row items-stretch ${bigView ? 'gap-0' : 'gap-6'} mb-3 lg:mb-6 min-h-0`}>
        <div
          className={`${bigView ? 'w-full' : 'lg:basis-3/5 w-full'} bg-black rounded-lg overflow-hidden relative hidden lg:flex items-center justify-center`}
          style={{ aspectRatio: `${viewROI.w} / ${viewROI.h}` }}
        >
          <div className='absolute top-2 right-2 z-10 flex gap-2'>
            <button
              type="button"
              onClick={() => setBigView(v => !v)}
              className="rounded-md px-3 py-1 text-xs text-white bg-white/10 hover:bg-white/20 transition-colors"
              aria-pressed={bigView}
              title={bigView ? '작게보기' : '크게보기'}
            >
              {bigView ? '작게보기' : '크게보기'}
            </button>

            <button
              type="button"
              onClick={() => setShowOverlay(v => !v)}
              className="rounded-md px-3 py-1 text-xs text-white bg-white/10 hover:bg-white/20 transition-colors"
              aria-pressed={showOverlay}
              title={showOverlay ? '오버레이 숨기기' : '오버레이 표시'}
            >
              {showOverlay ? '오버레이 숨기기' : '오버레이 표시'}
            </button>
          </div>
          <ScreenCapture
            videoRef={videoRef}
            overlayRef={overlayRef}
            viewROI={viewROI}
          />
        </div>

        {!bigView && (
          <>
            <div className="hidden lg:flex lg:flex-col lg:min-h-0 lg:basis-2/5 relative">
              {(step !== 'idle' && step !== 'done') && (
                <div className="absolute inset-0 z-20 flex items-center justify-center
                                bg-white/60 dark:bg-zinc-900/50 backdrop-blur-[2px]
                                pointer-events-auto">
                  <div className="px-4 py-2 rounded-md text-sm font-medium
                                  bg-white/90 dark:bg-zinc-900/90
                                  text-zinc-800 dark:text-zinc-200 shadow">
                    화면 공유 중에는 기록 패널을 사용할 수 없습니다
                  </div>
                </div>
              )}
              <SavedRaidsPanelLite
                records={visibleAnalyses}
                selectedIds={selectedIds}
                onSelect={handleSelect}
                onRefresh={loadAll}
                onLoadSelected={loadPartyRecord}
                onClearAll={clearAllPartyAnalyses}
                isLeader={isLeader}
                onCalcRaidAverage={handleCalcRaidAvg}
                onShowRaidAverage={handleShowRaidAvg}
              />
            </div>
          </>
        )}
      </div>

      <MobileSavedRaidsFab
        onOpen={() => setMobileSavedOpen(true)}
        shown={!mobileSavedOpen && fabShown}
      />


      {/* 현재 평균이 있으면 상단에 즉시 표시 */}
      {raidAvgCurrent && (
        <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-indigo-900 dark:bg-indigo-400/10 dark:border-indigo-400/20 dark:text-indigo-200">
          <div className="text-sm font-medium">
            현재 공격대 평균 ({raidAvgCurrent?.payload?.gateName || effectiveGate}) · 딜러 {raidAvgCurrent?.payload?.dealerCount}명
          </div>
          <div className="mt-1 flex flex-wrap gap-4 text-sm">
            <span>피해량 평균: <b>{formatByUnitForDisplay(raidAvgCurrent?.payload?.avgTotalRaw || 0, '피해량')}</b></span>
            <span>초당 피해량 평균: <b>{formatByUnitForDisplay(raidAvgCurrent?.payload?.avgDpsRaw || 0, '초당 피해량')}</b></span>
            <span>전투시간 평균: <b>{raidAvgCurrent?.payload?.battleTimeAvg}</b></span>
          </div>
        </div>
      )}



      <TraitsList title="메인 특성" items={mainView} />
      <TraitsList title="추가 특성" items={addView} />

      <MobileBottomSheet open={mobileSavedOpen} onClose={() => setMobileSavedOpen(false)}>
        <div className="flex flex-1 min-h-0 flex-col relative">
          <SavedRaidsPanelLite
            records={visibleAnalyses}
            selectedIds={selectedIds}
            onSelect={handleSelect}
            onRefresh={loadAll}
            onLoadSelected={() => { loadPartyRecord(); setMobileSavedOpen(false); }}
            onClearAll={clearAllPartyAnalyses}
            onDisband={disbandParty}
            isLeader={isLeader}
            onCalcRaidAverage={() => { handleCalcRaidAvg(); setMobileSavedOpen(false); }}
            onShowRaidAverage={handleShowRaidAvg}
          />
        </div>
      </MobileBottomSheet>


      {/* 스냅샷 모달 */}
      {raidAvgModalOpen && (

        <AppModal
          open={raidAvgModalOpen}
          onClose={() => setRaidAvgModalOpen(false)}
          title={`공격대 평균 확인 - ${(raidAvgCurrent?.payload?.gateName || effectiveGate || "-")}`}
          maxWidthClass="max-w-5xl"
          // footer OFF 하고 싶으면 footer={null} 유지
          footer={
            <button
              type="button"
              className="btn btn-primary w-full"
              onClick={() => setRaidAvgModalOpen(false)}
            >
              확인
            </button>
          }
        >
          {(raidAvgSnapshots || []).length === 0 ? (
            <div className="rounded-md border border-zinc-200 dark:border-zinc-800 p-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
              저장된 평균이 없습니다.
            </div>
          ) : (

            <div className="h-[240px] sm:h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />

                  <XAxis
                    dataKey="no"
                    tick={false}  // text-xs 느낌
                    axisLine={false}
                    tickLine={false}
                    tickMargin={6}
                  />

                  {/* 왼쪽 Y축: 총 피해량 */}
                  {/* 왼쪽 Y축: 총 피해량 (텍스트 숨김) */}
                  <YAxis
                    yAxisId="left"
                    domain={[leftMin, leftMax]}
                    tick={false}        // 라벨+틱 전부 숨김
                    tickLine={false}    // 틱선 숨김
                    // axisLine 기본색 사용 → prop 생략
                    width={0}           // 축 영역 최소화 (필요하면 8~12로)
                  />

                  {/* 오른쪽 Y축: DPS (텍스트 숨김) */}
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={[rightMin, rightMax]}
                    tick={false}
                    tickLine={false}
                    // axisLine 기본색 사용
                    width={0}
                  />

                  <Tooltip content={<TooltipContent />} />
                  <Legend
                    verticalAlign="top"
                    height={22}
                    iconType="circle"
                    iconSize={6}                 // ← 원 아이콘 크기 축소
                    wrapperStyle={{ fontSize: 14 }}  // text-sm
                  />

                  {/* 선 2개: 담백하게 */}
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="total"
                    name="피해량"
                    stroke="#6366F1"      // indigo-500
                    strokeWidth={2.5}
                    dot={{ r: 1.8, stroke: '#6366F1' }}
                    activeDot={{ r: 3.2 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="dps"
                    name="초당 피해량"
                    stroke="#F43F5E"      // rose-500
                    strokeWidth={2.5}
                    dot={{ r: 1.8, stroke: '#F43F5E' }}
                    activeDot={{ r: 3.2 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </AppModal>

      )}

    </div>
  );
}
